var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Node } from '../objects/node';
import { Connector, BezierSegment, StraightSegment } from '../objects/connector';
import { Point } from '../primitives/point';
import { BpmnSubEvent } from '../objects/node';
import { PointPort } from '../objects/port';
import { rotatePoint, cloneObject, randomId, getBounds } from '../utility/base-util';
import { Rect } from '../primitives/rect';
import { getFreeHandPath, getPolygonPath } from '../utility/path-util';
import { canOutConnect, canInConnect, canAllowDrop, canPortInConnect, canPortOutConnect, canMove } from '../utility/constraints-util';
import { transformPointByMatrix, rotateMatrix, identityMatrix } from '../primitives/matrix';
import { NodeConstraints, DiagramEvent, PortConstraints, DiagramConstraints, DiagramAction } from './../enum/enum';
import { TextElement } from '../core/elements/text-element';
import { contains } from './actions';
import { Selector } from '../objects/node';
import { getInOutConnectPorts, cloneBlazorObject, checkPort } from '../utility/diagram-util';
import { initializeCSPTemplate, isBlazor } from '@syncfusion/ej2-base';
import { findAngle } from '../utility/connector';
import { updateLaneBoundsWithSelector } from './container-interaction';
/**
 * Defines the interactive tools
 */
export class ToolBase {
    /**
     * Initializes the tool
     *
     * @param {CommandHandler} command Command that is corresponding to the current action
     * @param protectChange
     */
    constructor(command, protectChange = false) {
        /**
         * Command that is corresponding to the current action
         */
        this.commandHandler = null;
        // protected deepDiffer: DeepDiffMapper = new DeepDiffMapper();
        /**
         * Sets/Gets whether the interaction is being done
         */
        this.inAction = false;
        /**
         * Sets/Gets the protect change
         */
        this.isProtectChange = false;
        /**
         * Sets/Gets the current element that is under mouse
         */
        this.currentElement = null;
        /**   @private  */
        this.blocked = false;
        this.isTooltipVisible = false;
        /** @private */
        this.childTable = {};
        /**
         * Sets/Gets the previous object when mouse down
         */
        this.undoElement = { nodes: [], connectors: [] };
        this.checkProperty = true;
        this.undoParentElement = { nodes: [], connectors: [] };
        this.undoContainerElement = { nodes: [], connectors: [] };
        this.commandHandler = command;
        this.isProtectChange = protectChange;
    }
    startAction(currentElement) {
        this.currentElement = currentElement;
        this.inAction = true;
    }
    /**
     * @param args
     * @private
     */
    mouseDown(args) {
        this.currentElement = args.source;
        this.startPosition = this.currentPosition = this.prevPosition = args.position;
        this.isTooltipVisible = true;
        this.startAction(args.source);
        this.checkProperty = true;
        // Bug fix - EJ2-44495 -Node does not gets selected on slight movement of mouse when drag constraints disabled for node
        this.mouseDownElement = args.source;
    }
    checkPropertyValue() {
        if (this.checkProperty) {
            this.commandHandler.startTransaction(this.isProtectChange);
        }
    }
    /**
     * @param args
     * @private
     */
    mouseMove(args) {
        this.currentPosition = args.position;
        if (this.inAction) {
            this.commandHandler.startTransaction(this.isProtectChange);
            this.checkProperty = false;
        }
        //this.currentElement = currentElement;
        return !this.blocked;
    }
    /**
     * @param args
     * @private
     */
    mouseUp(args) {
        this.checkPropertyValue();
        this.currentPosition = args.position;
        // this.currentElement = currentElement;
        this.isTooltipVisible = false;
        this.commandHandler.endTransaction(this.isProtectChange);
        this.endAction();
        // Bug fix - EJ2-44495 -Node does not gets selected on slight movement of mouse when drag constraints disabled for node
        this.mouseDownElement = null;
    }
    endAction() {
        if (!this.isTooltipVisible) {
            this.commandHandler.closeTooltip();
        }
        this.commandHandler = null;
        this.currentElement = null;
        this.currentPosition = null;
        this.inAction = false;
        this.blocked = false;
    }
    /**
     * @param args
     * @private
     */
    mouseWheel(args) {
        this.currentPosition = args.position;
    }
    /**
     * @param args
     * @private
     */
    mouseLeave(args) {
        this.mouseUp(args);
    }
    updateSize(shape, startPoint, endPoint, corner, initialBounds, angle) {
        shape = this.commandHandler.renderContainerHelper(shape) || shape;
        const horizontalsnap = { snapped: false, offset: 0, left: false, right: false };
        const verticalsnap = { snapped: false, offset: 0, top: false, bottom: false };
        let difx = this.currentPosition.x - this.startPosition.x;
        let dify = this.currentPosition.y - this.startPosition.y;
        const snapEnabled = (!(shape instanceof TextElement)) && this.commandHandler.snappingModule
            && this.commandHandler.snappingModule.canSnap();
        const snapLine = snapEnabled ? this.commandHandler.snappingModule.getLayer() : null;
        const rotateAngle = (shape instanceof TextElement) ? angle : shape.rotateAngle;
        let matrix;
        matrix = identityMatrix();
        rotateMatrix(matrix, -rotateAngle, 0, 0);
        let x = shape.offsetX;
        let y = shape.offsetY;
        const w = shape.width;
        const h = shape.height;
        x = x - w * shape.pivot.x;
        y = y - h * shape.pivot.y;
        let deltaWidth = 0;
        let deltaHeight = 0;
        let diff;
        const width = (shape instanceof TextElement) ? shape.actualSize.width : shape.width;
        const height = (shape instanceof TextElement) ? shape.actualSize.height : shape.height;
        switch (corner) {
            case 'ResizeWest':
                diff = transformPointByMatrix(matrix, ({ x: difx, y: dify }));
                difx = diff.x;
                dify = diff.y;
                deltaHeight = 1;
                difx = snapEnabled ? this.commandHandler.snappingModule.snapLeft(horizontalsnap, verticalsnap, snapLine, difx, dify, shape, endPoint === startPoint, initialBounds) :
                    difx;
                dify = 0;
                deltaWidth = (initialBounds.width - difx) / width;
                break;
            case 'ResizeEast':
                diff = transformPointByMatrix(matrix, ({ x: difx, y: dify }));
                difx = diff.x;
                dify = diff.y;
                difx = snapEnabled ? this.commandHandler.snappingModule.snapRight(horizontalsnap, verticalsnap, snapLine, difx, dify, shape, endPoint === startPoint, initialBounds) :
                    difx;
                dify = 0;
                deltaWidth = (initialBounds.width + difx) / width;
                deltaHeight = 1;
                break;
            case 'ResizeNorth':
                deltaWidth = 1;
                diff = transformPointByMatrix(matrix, ({ x: difx, y: dify }));
                difx = diff.x;
                dify = diff.y;
                dify = snapEnabled ? this.commandHandler.snappingModule.snapTop(horizontalsnap, verticalsnap, snapLine, difx, dify, shape, endPoint === startPoint, initialBounds) :
                    dify;
                deltaHeight = (initialBounds.height - dify) / height;
                break;
            case 'ResizeSouth':
                deltaWidth = 1;
                diff = transformPointByMatrix(matrix, ({ x: difx, y: dify }));
                difx = diff.x;
                dify = diff.y;
                dify = snapEnabled ? this.commandHandler.snappingModule.snapBottom(horizontalsnap, verticalsnap, snapLine, difx, dify, shape, endPoint === startPoint, initialBounds) :
                    dify;
                deltaHeight = (initialBounds.height + dify) / height;
                break;
            case 'ResizeNorthEast':
                diff = transformPointByMatrix(matrix, ({ x: difx, y: dify }));
                difx = diff.x;
                dify = diff.y;
                difx = snapEnabled ? this.commandHandler.snappingModule.snapRight(horizontalsnap, verticalsnap, snapLine, difx, dify, shape, endPoint === startPoint, initialBounds) :
                    difx;
                dify = snapEnabled ? this.commandHandler.snappingModule.snapTop(horizontalsnap, verticalsnap, snapLine, difx, dify, shape, endPoint === startPoint, initialBounds) :
                    dify;
                deltaWidth = (initialBounds.width + difx) / width;
                deltaHeight = (initialBounds.height - dify) / height;
                break;
            case 'ResizeNorthWest':
                diff = transformPointByMatrix(matrix, ({ x: difx, y: dify }));
                difx = diff.x;
                dify = diff.y;
                dify = !snapEnabled ? dify : this.commandHandler.snappingModule.snapTop(horizontalsnap, verticalsnap, snapLine, difx, dify, shape, endPoint === startPoint, initialBounds);
                difx = !snapEnabled ? difx : this.commandHandler.snappingModule.snapLeft(horizontalsnap, verticalsnap, snapLine, difx, dify, shape, endPoint === startPoint, initialBounds);
                deltaWidth = (initialBounds.width - difx) / width;
                deltaHeight = (initialBounds.height - dify) / height;
                break;
            case 'ResizeSouthEast':
                diff = transformPointByMatrix(matrix, ({ x: difx, y: dify }));
                difx = diff.x;
                dify = diff.y;
                dify = !snapEnabled ? dify : this.commandHandler.snappingModule.snapBottom(horizontalsnap, verticalsnap, snapLine, difx, dify, shape, endPoint === startPoint, initialBounds);
                difx = !snapEnabled ? difx : this.commandHandler.snappingModule.snapRight(horizontalsnap, verticalsnap, snapLine, difx, dify, shape, endPoint === startPoint, initialBounds);
                deltaHeight = (initialBounds.height + dify) / height;
                deltaWidth = (initialBounds.width + difx) / width;
                break;
            case 'ResizeSouthWest':
                diff = transformPointByMatrix(matrix, ({ x: difx, y: dify }));
                difx = diff.x;
                dify = diff.y;
                dify = snapEnabled ? this.commandHandler.snappingModule.snapBottom(horizontalsnap, verticalsnap, snapLine, difx, dify, shape, endPoint === startPoint, initialBounds) : dify;
                difx = snapEnabled ? this.commandHandler.snappingModule.snapLeft(horizontalsnap, verticalsnap, snapLine, difx, dify, shape, endPoint === startPoint, initialBounds) : difx;
                deltaWidth = (initialBounds.width - difx) / width;
                deltaHeight = (initialBounds.height + dify) / height;
                break;
        }
        return { width: deltaWidth, height: deltaHeight };
    }
    getPivot(corner) {
        switch (corner) {
            case 'ResizeWest':
                return { x: 1, y: 0.5 };
            case 'ResizeEast':
                return { x: 0, y: 0.5 };
            case 'ResizeNorth':
                return { x: 0.5, y: 1 };
            case 'ResizeSouth':
                return { x: 0.5, y: 0 };
            case 'ResizeNorthEast':
                return { x: 0, y: 1 };
            case 'ResizeNorthWest':
                return { x: 1, y: 1 };
            case 'ResizeSouthEast':
                return { x: 0, y: 0 };
            case 'ResizeSouthWest':
                return { x: 1, y: 0 };
        }
        return { x: 0.5, y: 0.5 };
    }
    //method to get node shape name
    getShapeType() {
        let shape;
        // eslint-disable-next-line no-constant-condition
        if (this.commandHandler.diagram.drawingObject.shape.type === 'Image' ||
            this.commandHandler.diagram.drawingObject.shape.type === 'HTML' ||
            this.commandHandler.diagram.drawingObject.shape.type === 'Native' ||
            this.commandHandler.diagram.drawingObject.shape.type === 'Path') {
            shape = this.commandHandler.diagram.drawingObject.shape.type;
        }
        else {
            shape = this.commandHandler.diagram.drawingObject.shape.shape;
        }
        return shape;
    }
    //EJ2-52203-Method to trigger ElementDraw Event when we draw node or connector with the drawing Tool
    triggerElementDrawEvent(source, state, objectType, elementType, isMouseDownAction) {
        let arg = {
            source: source, state: state, objectType: objectType, cancel: false, elementType: elementType
        };
        this.commandHandler.triggerEvent(DiagramEvent.elementDraw, arg);
        if (isMouseDownAction && arg.cancel) {
            {
                this.commandHandler.diagram.resetTool();
                this.inAction = false;
            }
        }
    }
}
/**
 * Helps to select the objects
 */
export class SelectTool extends ToolBase {
    constructor(commandHandler, protectChange, action) {
        super(commandHandler, true);
        this.action = action;
    }
    /**
     * @param args
     * @private
     */
    mouseDown(args) {
        this.inAction = true;
        super.mouseDown(args);
    }
    /**
     * @param args
     * @private
     */
    mouseMove(args) {
        super.mouseMove(args);
        //draw selected region
        if (this.inAction && Point.equals(this.currentPosition, this.prevPosition) === false) {
            const rect = Rect.toBounds([this.prevPosition, this.currentPosition]);
            // Bug fix - EJ2-44495 -Node does not gets selected on slight movement of mouse when drag constraints disabled for node
            if (this.mouseDownElement && !canMove(this.mouseDownElement)) {
                this.commandHandler.clearObjectSelection(this.mouseDownElement);
            }
            else {
                this.commandHandler.clearSelectedItems();
                this.commandHandler.drawSelectionRectangle(rect.x, rect.y, rect.width, rect.height);
            }
        }
        return !this.blocked;
    }
    /**
     * @param args
     * @private
     */
    mouseUp(args, button) {
        this.checkPropertyValue();
        //rubber band selection
        if (!this.commandHandler.isUserHandle(this.currentPosition)) {
            if (Point.equals(this.currentPosition, this.prevPosition) === false && this.inAction) {
                const region = Rect.toBounds([this.prevPosition, this.currentPosition]);
                this.commandHandler.doRubberBandSelection(region);
            }
            else {
                //single selection
                const arrayNodes = this.commandHandler.getSelectedObject();
                if (!this.commandHandler.hasSelection() || !args.info || !args.info.ctrlKey) {
                    // 948882: Improper Selection Behavior When Node Drag Constraint is Disabled
                    if (button !== 2 || !arrayNodes.some((obj) => obj === args.source)) {
                        this.commandHandler.clearSelection(args.source === null ? true : false);
                        if (this.action === 'LabelSelect') {
                            this.commandHandler.labelSelect(args.source, args.sourceWrapper, arrayNodes);
                        }
                        else if (args.source) {
                            this.commandHandler.selectObjects([args.source], false, arrayNodes);
                        }
                    }
                }
                else {
                    //handling multiple selection
                    if (args && args.source) {
                        if (!this.commandHandler.isSelected(args.source)) {
                            this.commandHandler.selectObjects([args.source], true);
                        }
                        else {
                            if (args.clickCount === 1) {
                                this.commandHandler.unSelect(args.source);
                                // this.commandHandler.updateBlazorSelector();
                            }
                        }
                    }
                }
            }
        }
        this.inAction = false;
        super.mouseUp(args);
    }
    /**
     * @param args
     * @private
     */
    mouseLeave(args) {
        if (this.inAction) {
            this.mouseUp(args);
        }
    }
}
export class FixedUserHandleTool extends ToolBase {
    /**
     * @param args
     * @private
     */
    mouseUp(args) {
        this.checkPropertyValue();
        this.inAction = false;
        const val = args.source;
        let fixedUserHandle;
        const iconId = args.sourceWrapper.id;
        for (let i = 0; i < val.fixedUserHandles.length; i++) {
            if (iconId.indexOf(val.fixedUserHandles[parseInt(i.toString(), 10)].id) > -1) {
                fixedUserHandle = val.fixedUserHandles[parseInt(i.toString(), 10)];
            }
        }
        const arg = {
            fixedUserHandle: fixedUserHandle,
            element: args.source
        };
        const trigger = DiagramEvent.fixedUserHandleClick;
        this.commandHandler.triggerEvent(trigger, arg);
        super.mouseUp(args);
    }
}
/**
 * Helps to edit the selected connectors
 */
export class ConnectTool extends ToolBase {
    constructor(commandHandler, endPoint) {
        super(commandHandler, true);
        this.isConnected = false;
        this.endPoint = endPoint;
    }
    /**
     * @param args
     * @private
     */
    mouseDown(args) {
        const _super = Object.create(null, {
            mouseDown: { get: () => super.mouseDown }
        });
        return __awaiter(this, void 0, void 0, function* () {
            this.inAction = true;
            this.undoElement = undefined;
            if (!(this instanceof ConnectorDrawingTool)) {
                this.undoElement = cloneObject(args.source);
            }
            _super.mouseDown.call(this, args);
            let oldValue;
            let connectors;
            if (args.source && args.source.connectors) {
                oldValue = { x: this.prevPosition.x, y: this.prevPosition.y };
                connectors = args.source.connectors[0];
                this.oldConnector = cloneObject(connectors);
            }
            // Sets the selected segment
            if (this.endPoint === 'BezierSourceThumb' || this.endPoint === 'BezierTargetThumb') {
                for (let i = 0; i < connectors.segments.length; i++) {
                    const segment = connectors.segments[parseInt(i.toString(), 10)];
                    const segmentpoint1 = !Point.isEmptyPoint(segment.point1) ? segment.point1 : segment.bezierPoint1;
                    const segmentpoint2 = !Point.isEmptyPoint(segment.point2) ? segment.point2 : segment.bezierPoint2;
                    //(EJ2-70650)-Unable to drag bezier control thumb, when we increase handleSize value
                    //Added below code for drag the bezier control thumb while increasing handle size(For hitPadding)
                    if (this.currentElement.handleSize !== connectors.hitPadding) {
                        connectors.hitPadding = this.currentElement.handleSize;
                    }
                    if (contains(this.currentPosition, segmentpoint1, connectors.hitPadding) ||
                        contains(this.currentPosition, segmentpoint2, connectors.hitPadding)) {
                        this.selectedSegment = segment;
                    }
                }
            }
            this.currentPosition = args.position;
        });
    }
    /**
     * @param args
     * @private
     */
    mouseUp(args) {
        const _super = Object.create(null, {
            mouseUp: { get: () => super.mouseUp }
        });
        return __awaiter(this, void 0, void 0, function* () {
            if (!isBlazor() && this.isConnected && args.source.connectors) {
                const connector = args.source.connectors[0];
                const nodeEndId = this.endPoint === 'ConnectorSourceEnd' ? 'sourceID' : 'targetID';
                const portEndId = this.endPoint === 'ConnectorSourceEnd' ? 'sourcePortID' : 'targetPortID';
                const arg = {
                    connector: cloneBlazorObject(connector),
                    oldValue: { nodeId: this.oldConnector[`${nodeEndId}`], portId: this.oldConnector[`${portEndId}`] },
                    newValue: { nodeId: connector[`${nodeEndId}`], portId: connector[`${portEndId}`] }, cancel: false,
                    state: 'Changed', connectorEnd: this.endPoint
                };
                //875655- ConnectionChange Event not triggered in Changed state for port change in same node
                if (connector[`${nodeEndId}`] !== this.oldConnector[`${nodeEndId}`] || connector[`${portEndId}`] !== this.oldConnector[`${portEndId}`]) {
                    this.commandHandler.triggerEvent(DiagramEvent.connectionChange, arg);
                    this.isConnected = false;
                }
            }
            this.checkPropertyValue();
            this.commandHandler.updateSelector();
            this.commandHandler.removeSnap();
            if ((!(this instanceof ConnectorDrawingTool)) && ((this.endPoint === 'ConnectorSourceEnd' &&
                args.source.connectors.length &&
                ((!Point.equals(args.source.connectors[0].sourcePoint, this.undoElement.connectors[0].sourcePoint) ||
                    (args.source.connectors[0].sourceID !== this.undoElement.connectors[0].sourceID)))) ||
                (this.endPoint === 'ConnectorTargetEnd' &&
                    ((!Point.equals(args.source.connectors[0].targetPoint, this.undoElement.connectors[0].targetPoint))
                        || (args.source.connectors[0].targetID !== this.undoElement.connectors[0].targetID))))) {
                let oldValues;
                let newValues;
                let connector;
                if (args.source && args.source.connectors && this.endPoint === 'ConnectorSourceEnd') {
                    //941055: The sourcePointChange event's old and new values are the same
                    oldValues = { x: this.oldConnector.sourcePoint.x, y: this.oldConnector.sourcePoint.y };
                    connector = args.source.connectors[0];
                    newValues = { x: connector.sourcePoint.x, y: connector.sourcePoint.y };
                }
                else if (args.source && args.source.connectors && this.endPoint === 'ConnectorTargetEnd') {
                    oldValues = { x: this.oldConnector.targetPoint.x, y: this.oldConnector.targetPoint.y };
                    connector = args.source.connectors[0];
                    newValues = { x: connector.targetPoint.x, y: connector.targetPoint.y };
                }
                let targetPortName;
                let targetNodeNode;
                if (args.target) {
                    const target = this.commandHandler.findTarget(args.targetWrapper, args.target, this.endPoint === 'ConnectorSourceEnd', true);
                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                    targetNodeNode = target.id;
                    if (target instanceof PointPort) {
                        //941055: The target node is undefined while connected to the port
                        targetPortName = target.id;
                        targetNodeNode = args.target.id;
                    }
                }
                // 947614: Property Change does not log into history within connection change event completed state
                if (this.commandHandler.diagram.diagramActions & DiagramAction.ToolAction) {
                    this.commandHandler.diagram.diagramActions &= ~DiagramAction.ToolAction;
                }
                let arg = {
                    connector: connector, state: 'Completed', targetNode: targetNodeNode,
                    oldValue: oldValues, newValue: newValues, cancel: false, targetPort: targetPortName
                };
                if (this.undoElement && args.source) {
                    // eslint-disable-next-line prefer-const
                    let obj;
                    obj = cloneObject(args.source);
                    const entry = {
                        type: 'ConnectionChanged', redoObject: cloneObject(obj), undoObject: cloneObject(this.undoElement),
                        category: 'Internal'
                    };
                    this.commandHandler.addHistoryEntry(entry);
                }
                const trigger = this.endPoint === 'ConnectorSourceEnd' ? DiagramEvent.sourcePointChange : DiagramEvent.targetPointChange;
                this.commandHandler.triggerEvent(trigger, arg);
                this.commandHandler.removeTerminalSegment(connector, true);
            }
            else if (!(this instanceof ConnectorDrawingTool) &&
                (this.endPoint === 'BezierTargetThumb' || this.endPoint === 'BezierSourceThumb')) {
                if (this.undoElement && args.source) {
                    const obj = cloneObject(args.source);
                    const entry = {
                        type: 'SegmentChanged', redoObject: obj, undoObject: this.undoElement, category: 'Internal'
                    };
                    this.commandHandler.addHistoryEntry(entry);
                }
            }
            // this.commandHandler.updateBlazorSelector();
            this.canCancel = undefined;
            this.tempArgs = undefined;
            //(EJ2-66201) - Exception occurs when mouse-hover on ports in node
            if (args.source && args.source.connectors) {
                let connector = args.source.connectors[0];
                if (connector.isBezierEditing) {
                    connector.isBezierEditing = false;
                }
            }
            _super.mouseUp.call(this, args);
        });
    }
    /* tslint:disable */
    /**
     * @param args
     * @private
     */
    mouseMove(args) {
        super.mouseMove(args);
        let tempArgs;
        if ((!(this instanceof ConnectorDrawingTool)) && ((this.endPoint === 'ConnectorSourceEnd' &&
            Point.equals(args.source.connectors[0].sourcePoint, this.undoElement.connectors[0].sourcePoint)) ||
            (this.endPoint === 'ConnectorTargetEnd' &&
                Point.equals(args.source.connectors[0].targetPoint, this.undoElement.connectors[0].targetPoint)))) {
            let oldValue;
            let connectors;
            if (args.source && args.source.connectors) {
                oldValue = { x: this.prevPosition.x, y: this.prevPosition.y };
                connectors = args.source.connectors[0];
            }
            let targetPort;
            let targetNode;
            if (args.target) {
                targetNode = args.target.id;
                const target = this.commandHandler.findTarget(args.targetWrapper, args.target, this.endPoint === 'ConnectorSourceEnd', true);
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                (target instanceof PointPort || target instanceof BpmnSubEvent) ? targetPort = target.id : targetNode = target.id;
            }
            let arg = {
                connector: connectors, state: 'Start', targetNode: targetNode,
                oldValue: oldValue, newValue: oldValue, cancel: false, targetPort: targetPort
            };
            const trigger = this.endPoint === 'ConnectorSourceEnd' ?
                DiagramEvent.sourcePointChange : DiagramEvent.targetPointChange;
            this.commandHandler.triggerEvent(trigger, arg);
        }
        this.currentPosition = args.position;
        if (this.currentPosition && this.prevPosition) {
            const diffX = this.currentPosition.x - this.prevPosition.x;
            const diffY = this.currentPosition.y - this.prevPosition.y;
            let newValue;
            let oldValue;
            let inPort;
            let outPort;
            this.currentPosition = this.commandHandler.snapConnectorEnd(this.currentPosition);
            let connector;
            if (args.source && args.source.connectors) {
                newValue = { x: this.currentPosition.x, y: this.currentPosition.y };
                oldValue = { x: this.prevPosition.x, y: this.prevPosition.y };
                connector = args.source.connectors[0];
            }
            let targetPortId;
            let targetNodeId;
            if (args.target) {
                const target = this.commandHandler.findTarget(args.targetWrapper, args.target, this.endPoint === 'ConnectorSourceEnd', true);
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                (target instanceof PointPort) ? targetPortId = target.id : targetNodeId = target.id;
            }
            let arg = {
                connector: connector, state: 'Progress', targetNode: targetNodeId,
                oldValue: oldValue, newValue: newValue, cancel: false, targetPort: targetPortId
            };
            if (!(this instanceof ConnectorDrawingTool)) {
                const trigger = this.endPoint === 'ConnectorSourceEnd' ?
                    DiagramEvent.sourcePointChange : DiagramEvent.targetPointChange;
                this.commandHandler.triggerEvent(trigger, arg);
            }
            if (args.target) {
                inPort = getInOutConnectPorts(args.target, true);
                outPort = getInOutConnectPorts(args.target, false);
            }
            if (!arg.cancel && this.inAction && this.endPoint !== undefined && diffX !== 0 || diffY !== 0) {
                // EJ2-65331 - The condition checks whether the cancel argument is true or false
                if (!arg.cancel) {
                    this.blocked = !this.commandHandler.dragConnectorEnds(this.endPoint, args.source, this.currentPosition, this.selectedSegment, args.target, targetPortId);
                    this.commandHandler.updateSelector();
                }
                if (args.target && ((this.endPoint === 'ConnectorSourceEnd' && (canOutConnect(args.target) || canPortOutConnect(outPort)))
                    || (this.endPoint === 'ConnectorTargetEnd' && (canInConnect(args.target) || canPortInConnect(inPort))))) {
                    if (this.commandHandler.canDisconnect(this.endPoint, args, targetPortId, targetNodeId)) {
                        tempArgs = this.commandHandler.disConnect(args.source, this.endPoint, this.canCancel);
                        this.isConnected = true;
                    }
                    const target = this.commandHandler.findTarget(args.targetWrapper, args.target, this.endPoint === 'ConnectorSourceEnd', true);
                    if (target instanceof Node) {
                        if ((canInConnect(target) && this.endPoint === 'ConnectorTargetEnd')
                            || (canOutConnect(target) && this.endPoint === 'ConnectorSourceEnd')) {
                            tempArgs = this.commandHandler.connect(this.endPoint, args, this.canCancel);
                            this.isConnected = true;
                        }
                    }
                    else {
                        const isConnect = this.checkConnect(target);
                        if (isConnect) {
                            this.isConnected = true;
                            tempArgs = this.commandHandler.connect(this.endPoint, args, this.canCancel);
                        }
                    }
                }
                else if (this.endPoint.indexOf('Bezier') === -1) {
                    this.isConnected = true;
                    tempArgs = this.commandHandler.disConnect(args.source, this.endPoint, this.canCancel);
                    this.commandHandler.updateSelector();
                }
            }
            if (this.commandHandler.canEnableDefaultTooltip()) {
                const content = this.getTooltipContent(args.position);
                const contentTemp = function () {
                    return content;
                };
                this.commandHandler.showTooltip(args.source, args.position, initializeCSPTemplate(contentTemp), 'ConnectTool', this.isTooltipVisible);
                this.isTooltipVisible = false;
            }
            if (tempArgs) {
                this.tempArgs = tempArgs;
            }
        }
        this.prevPosition = this.currentPosition;
        return !this.blocked;
    }
    /**
     * @param args
     * @private
     */
    mouseLeave(args) {
        this.mouseUp(args);
    }
    getTooltipContent(position) {
        return 'X:' + Math.round(position.x) + ' ' + 'Y:' + Math.round(position.y);
    }
    checkConnect(target) {
        if (canPortInConnect(target) && this.endPoint === 'ConnectorTargetEnd') {
            return true;
        }
        else if (canPortOutConnect(target) && this.endPoint === 'ConnectorSourceEnd') {
            return true;
        }
        else if (!(target.constraints & PortConstraints.None) && !canPortInConnect(target) && !canPortOutConnect(target)
            && (target.constraints === undefined || (target.constraints & (PortConstraints.Default & ~(PortConstraints.InConnect | PortConstraints.OutConnect))) > 0)) {
            return true;
        }
        return false;
    }
    /**   @private  */
    endAction() {
        super.endAction();
        this.prevPosition = null;
        this.endPoint = null;
    }
}
/**
 * Drags the selected objects
 */
export class MoveTool extends ToolBase {
    constructor(commandHandler, objType) {
        super(commandHandler, true);
        /**   @private  */
        this.currentTarget = null;
        this.isStartAction = false;
        this.canCancel = false;
        this.canTrigger = false;
        this.objectType = objType;
    }
    /**
     * @param args
     * @private
     */
    mouseDown(args) {
        if (args.source instanceof Node || args.source instanceof Connector) {
            const arrayNodes = this.commandHandler.getSelectedObject();
            this.commandHandler.selectObjects([args.source], args.info && args.info.ctrlKey, arrayNodes);
            const selectedObject = { nodes: [], connectors: [] };
            if (args.source instanceof Node) {
                selectedObject.nodes.push(cloneObject(args.source));
            }
            else {
                selectedObject.connectors.push(cloneObject(args.source));
            }
            this.undoElement = cloneObject(selectedObject);
            //909582-History change event args old value is undefined upon node drag
            const wrapper = args.source.wrapper;
            this.undoElement.offsetX = wrapper.offsetX;
            this.undoElement.offsetY = wrapper.offsetY;
        }
        else {
            this.undoElement = cloneObject(args.source);
        }
        //951087-Undo function doesn't retain connector segments after node move actions.
        if (this.undoElement.nodes && this.undoElement.nodes.length > 0) {
            for (const node of this.undoElement.nodes) {
                for (const edgeId of [...node.outEdges, ...node.inEdges]) {
                    const connector = this.commandHandler.diagram.getObject(edgeId);
                    if (connector && connector.segments && connector.segments.length > 1) {
                        if (this.undoElement.connectors.indexOf(connector) === -1) {
                            this.undoElement.connectors.push(cloneObject(connector));
                        }
                    }
                }
            }
        }
        this.undoParentElement = this.commandHandler.getSubProcess(args.source);
        this.undoContainerElement = this.commandHandler.getContainer(args.source);
        if (this.objectType === 'Port') {
            this.portId = args.sourceWrapper.id;
        }
        this.commandHandler.insertBlazorConnector(args.source);
        super.mouseDown(args);
        this.initialOffset = { x: 0, y: 0 };
    }
    getPort(args) {
        let port;
        const sourceId = args.source.id;
        const sourcePorts = args.source.ports;
        for (let i = 0; i < sourcePorts.length; i++) {
            if (args.sourceWrapper.id === sourceId + '_' + sourcePorts[parseInt(i.toString(), 10)].id) {
                port = sourcePorts[parseInt(i.toString(), 10)];
                break;
            }
        }
        return port;
    }
    /* tslint:disable */
    /**
     * @param args
     * @param isPreventHistory
     * @param args
     * @param isPreventHistory
     * @private
     */
    mouseUp(args, isPreventHistory) {
        const _super = Object.create(null, {
            mouseUp: { get: () => super.mouseUp }
        });
        return __awaiter(this, void 0, void 0, function* () {
            let oldValues;
            let newValues;
            this.checkPropertyValue();
            let obj;
            let historyAdded = false;
            let object;
            const redoObject = { nodes: [], connectors: [] };
            if (this.objectType !== 'Port') {
                if (args.source instanceof Node || args.source instanceof Connector) {
                    if (args.source instanceof Node) {
                        redoObject.nodes.push(cloneObject(args.source));
                    }
                    else {
                        redoObject.connectors.push(cloneObject(args.source));
                    }
                    obj = cloneObject(redoObject);
                    const wrapper = args.source.wrapper;
                    obj.offsetX = wrapper.offsetX;
                    obj.offsetY = wrapper.offsetY;
                }
                else {
                    obj = cloneObject(args.source);
                }
                //951087-Undo funciton doesn't retain connector segments after node move actions.
                if (obj.nodes && obj.nodes.length > 0) {
                    for (const node of obj.nodes) {
                        for (const edgeId of [...node.outEdges, ...node.inEdges]) {
                            const connector = this.commandHandler.diagram.getObject(edgeId);
                            if (this.undoElement.connectors.indexOf(connector) !== -1 && obj.connectors.indexOf(connector) === -1) {
                                const connectorClone = cloneObject(connector);
                                redoObject.connectors.push(connectorClone);
                                obj.connectors.push(connectorClone);
                            }
                        }
                    }
                }
                object = this.commandHandler.renderContainerHelper(args.source) || args.source || this.commandHandler.renderContainerHelper(args.source);
                if ((object.id === 'helper') || (object.id !== 'helper')) {
                    let isSubGroupSelection = false;
                    if (object instanceof Selector) {
                        let currentSelection = cloneObject(object);
                        // check currentSelection.selectedObjects array contains same object of this.undoElement.selectedObjects array
                        if (currentSelection.selectedObjects.length === this.undoElement.selectedObjects.length) {
                            for (let i = 0; i < currentSelection.selectedObjects.length; i++) {
                                if (currentSelection.selectedObjects[parseInt(i.toString(), 10)].id !==
                                    this.undoElement.selectedObjects[parseInt(i.toString(), 10)].id) {
                                    isSubGroupSelection = true;
                                    break;
                                }
                            }
                        }
                    }
                    //EJ2-71257 - Position change event completed state is not fired on selecting the node first and then dragging the node while changing node width at progress state.
                    // If object is instanceof selector then checked the length of selected objects is 1 or not.
                    const isSelector = object instanceof Selector;
                    const isSingleSelectedObject = object instanceof Selector && object.selectedObjects && object.selectedObjects.length === 1;
                    const isSameSize = object instanceof Selector && Math.round(object.width) === Math.round(this.undoElement.width) && Math.round(object.height) === Math.round(this.undoElement.height);
                    const isDifferentPosition = object.offsetX !== this.undoElement.offsetX || object.offsetY !== this.undoElement.offsetY;
                    const isDifferentSourcePoint = object.sourcePoint !== this.undoElement.sourcePoint;
                    const isDifferentTargetPoint = object.targetPoint !== this.undoElement.targetPoint;
                    if (!isSubGroupSelection &&
                        (((isSelector && (isSameSize || isSingleSelectedObject)) || !isSelector) && (isDifferentPosition || isDifferentSourcePoint || isDifferentTargetPoint))
                        || this.isSelectionHasConnector(object)) {
                        if (args.source) {
                            newValues = { offsetX: args.source.wrapper.offsetX, offsetY: args.source.wrapper.offsetY };
                            oldValues = { offsetX: args.source.wrapper.offsetX, offsetY: args.source.wrapper.offsetY };
                        }
                        let arg = {
                            source: args.source, state: 'Completed', oldValue: this.intialValue, newValue: newValues,
                            target: this.currentTarget, targetPosition: this.currentPosition, allowDrop: true, cancel: false
                        };
                        arg = {
                            source: cloneBlazorObject(args.source), state: 'Completed',
                            oldValue: cloneBlazorObject(this.intialValue), newValue: cloneBlazorObject(newValues),
                            target: cloneBlazorObject(this.currentTarget), targetPosition: cloneBlazorObject(this.currentPosition),
                            allowDrop: arg.allowDrop, cancel: arg.cancel
                        };
                        // EJ2-824712 [Bug] - When selecting swimlane second time history change event is triggered with change type as positionChanged.
                        let canAddHistory = true;
                        //EJ2-69852): Position Change event trigger for clicking second time in swimlane header issue
                        if (object.id === 'helper') {
                            if (this.canTrigger) {
                                //EJ2-925499 - Undo/Redo not working after moveing multiselected nodes inside swimlane while line routing enabled
                                if ((obj.nodes.length > 1 || obj.connectors.length > 1) && (this.commandHandler.diagram.lineRoutingModule &&
                                    (this.commandHandler.diagram.constraints & DiagramConstraints.LineRouting))) {
                                    const nameTable = this.commandHandler.diagram.nameTable;
                                    for (let i = 0; i < obj.nodes.length; i++) {
                                        if (!(nameTable[obj.nodes[parseInt(i.toString(), 10)].parentId] &&
                                            nameTable[obj.nodes[parseInt(i.toString(), 10)].parentId].isLane)) {
                                            canAddHistory = false;
                                            break;
                                        }
                                    }
                                }
                                else {
                                    canAddHistory = true;
                                }
                                this.commandHandler.triggerEvent(DiagramEvent.positionChange, arg);
                                this.connectorEndPointChangeEvent(arg);
                            }
                            else {
                                canAddHistory = false;
                            }
                        }
                        else {
                            this.commandHandler.triggerEvent(DiagramEvent.positionChange, arg);
                            this.connectorEndPointChangeEvent(arg);
                        }
                        if (!isPreventHistory && canAddHistory) {
                            this.commandHandler.startGroupAction();
                            historyAdded = true;
                            const entry = {
                                type: 'PositionChanged',
                                redoObject: cloneObject(obj), undoObject: cloneObject(this.undoElement), category: 'Internal'
                            };
                            if (obj.nodes[0] && obj.nodes[0].processId) {
                                const entry = {
                                    type: 'SizeChanged', category: 'Internal',
                                    undoObject: this.undoParentElement, redoObject: this.commandHandler.getSubProcess(args.source)
                                };
                                this.commandHandler.addHistoryEntry(entry);
                            }
                            if (obj.nodes[0] && obj.nodes[0].parentId
                                && this.commandHandler.diagram.nameTable[obj.nodes[0].parentId].shape.type === 'Container') {
                                const entry = {
                                    type: 'SizeChanged', category: 'Internal',
                                    undoObject: this.undoContainerElement, redoObject: this.commandHandler.getContainer(args.source)
                                };
                                this.commandHandler.addHistoryEntry(entry);
                            }
                            this.commandHandler.addHistoryEntry(entry);
                        }
                    }
                }
                const snappedPoint = this.commandHandler.snapPoint(this.prevPosition, this.currentPosition, 0, 0);
                this.commandHandler.removeSnap();
                this.commandHandler.removeHighlighter();
                if (args.source && this.currentTarget && canAllowDrop(this.currentTarget) &&
                    this.commandHandler.isDroppable(args.source, this.currentTarget)) {
                    let canSplit = true;
                    this.commandHandler.drop(this.currentElement, this.currentTarget, this.currentPosition);
                    if (this.currentTarget && this.currentTarget instanceof Connector) {
                        if (this.commandHandler.diagram.enableConnectorSplit === true) {
                            //EJ2-894577- Restricting the connector splitting if any one edge is already connected to the node
                            if (this.currentElement && this.currentElement instanceof Node) {
                                if (this.currentElement.children !== undefined) {
                                    for (let i = 0; i < this.currentElement.children.length; i++) {
                                        if (this.currentElement.children[parseInt(i.toString(), 10)] === this.currentTarget.sourceID ||
                                            this.currentElement.children[parseInt(i.toString(), 10)] === this.currentTarget.targetID) {
                                            canSplit = false;
                                        }
                                    }
                                }
                                else {
                                    if (this.currentElement.id === this.currentTarget.sourceID || this.currentElement.id === this.currentTarget.targetID) {
                                        canSplit = false;
                                    }
                                }
                                if (canSplit) {
                                    this.commandHandler.connectorSplit(this.currentElement, this.currentTarget);
                                }
                            }
                            else if (this.currentElement instanceof Selector && !(this.commandHandler.PreventConnectorSplit)) {
                                if (this.currentElement.nodes[0].children) {
                                    for (let i = 0; i < this.currentElement.nodes[0].children.length; i++) {
                                        if (this.currentElement.nodes[0].children[parseInt(i.toString(), 10)] === this.currentTarget.sourceID ||
                                            this.currentElement.nodes[0].children[parseInt(i.toString(), 10)] === this.currentTarget.targetID) {
                                            canSplit = false;
                                        }
                                    }
                                }
                                else {
                                    if ((this.currentElement.nodes[0]).id === this.currentTarget.sourceID ||
                                        (this.currentElement.nodes[0]).id === this.currentTarget.targetID) {
                                        canSplit = false;
                                    }
                                }
                                if (canSplit) {
                                    this.commandHandler.connectorSplit(this.currentElement.nodes[0], this.currentTarget);
                                    this.commandHandler.PreventConnectorSplit = false;
                                }
                            }
                        }
                    }
                    let arg = {
                        element: args.source, target: this.currentTarget, position: this.currentPosition, cancel: false
                    };
                    this.commandHandler.triggerEvent(DiagramEvent.drop, arg);
                    if (!arg.cancel && args.source && this.commandHandler.isParentAsContainer(this.currentTarget) && !this.commandHandler.isTargetSubProcess(this.currentTarget)) {
                        let nodes = (args.source instanceof Selector) ? args.source.nodes : [args.source];
                        let isEndGroup = false;
                        let temp;
                        for (let i = 0; i < nodes.length; i++) {
                            if (nodes[0].parentId === nodes[parseInt(i.toString(), 10)].parentId) {
                                temp = true;
                            }
                            else {
                                temp = false;
                                break;
                            }
                        }
                        // 902192: Diagram node resized wrongly while dragging and drop on multiple selected nodes in another lane Issue Fix
                        if (this.commandHandler.diagram.selectedItems.nodes.length !== nodes.length) {
                            nodes = this.commandHandler.diagram.selectedItems.nodes;
                        }
                        if (this.commandHandler.diagram.selectedItems.nodes.length > 1) {
                            //929543: To calculate the difference between the target lane bounds and selector bounds.
                            // We use this difference values to set the margin left and margin top for the child nodes of lane.
                            nodes = this.calculateDiff(this.commandHandler.diagram.selectedItems, this.currentTarget, this.commandHandler.diagram);
                            this.commandHandler.diagram.multiselect = true;
                        }
                        else {
                            this.commandHandler.diagram.multiselect = false;
                        }
                        for (let i = 0; i < nodes.length; i++) {
                            if (!nodes[parseInt(i.toString(), 10)].container && !(this.commandHandler.diagram.cancelPositionChange)) {
                                isEndGroup = true;
                                this.commandHandler.updateLaneChildrenZindex(nodes[parseInt(i.toString(), 10)], this.currentTarget);
                                this.commandHandler.dropChildToContainer(this.currentTarget, nodes[parseInt(i.toString(), 10)]);
                                this.commandHandler.renderContainerHelper(nodes[parseInt(i.toString(), 10)]);
                            }
                        }
                        //929543: To update the lane size based on the dropped child nodes entire bounds.
                        if (nodes.length > 1) {
                            let helper = this.commandHandler.diagram.nameTable['helper'];
                            if (helper) {
                                updateLaneBoundsWithSelector(this.currentTarget, helper, this.commandHandler.diagram);
                            }
                        }
                        if (historyAdded && this.commandHandler.isContainer && isEndGroup) {
                            this.commandHandler.endGroupAction();
                        }
                    }
                }
                if (args.source && this.currentTarget) {
                    this.commandHandler.dropAnnotation(args.source, this.currentTarget);
                }
                this.commandHandler.updateSelector();
                if (historyAdded && !this.commandHandler.isContainer) {
                    this.commandHandler.endGroupAction();
                }
            }
            else {
                redoObject.nodes.push(cloneObject(args.source));
                args.portId = this.portId;
                obj = cloneObject(redoObject);
                const entry = {
                    type: 'PortPositionChanged', objectId: this.portId,
                    redoObject: cloneObject(obj), undoObject: cloneObject(this.undoElement), category: 'Internal'
                };
                this.commandHandler.addHistoryEntry(entry);
                //EJ2-909578: - Position change incorrect event args value for port dragging
                //Required correction on data type
                let port = this.getPort(args);
                if (port) {
                    let nodePorts = this.undoElement.nodes.length > 0 ? this.undoElement.nodes[0].ports : [];
                    let connPorts = this.undoElement.connectors.length > 0 ? this.undoElement.connectors[0].ports : [];
                    for (let i = 0; i < nodePorts.length; i++) {
                        if (port.id === nodePorts[parseInt(i.toString(), 10)].id) {
                            oldValues = {
                                offsetX: nodePorts[parseInt(i.toString(), 10)].offset.x,
                                offsetY: nodePorts[parseInt(i.toString(), 10)].offset.y
                            };
                            newValues = { offsetX: port.offset.x, offsetY: port.offset.y };
                            break;
                        }
                    }
                    for (let i = 0; i < connPorts.length; i++) {
                        if (port.id === connPorts[parseInt(i.toString(), 10)].id) {
                            oldValues = {
                                offset: connPorts[parseInt(i.toString(), 10)].offset
                            };
                            newValues = { offset: port.offset };
                            break;
                        }
                    }
                }
                let arg = {
                    source: port, state: 'Completed', oldValue: oldValues, newValue: newValues,
                    target: this.currentTarget, targetPosition: this.currentPosition, allowDrop: true, cancel: false
                };
                this.commandHandler.triggerEvent(DiagramEvent.positionChange, arg);
            }
            // this.commandHandler.updateBlazorSelector();
            _super.mouseUp.call(this, args);
        });
    }
    clearDiff(nodes) {
        nodes.forEach(function (node, index) {
            delete node.diffX;
            delete node.diffY;
        });
    }
    calculateDiff(selector, target, diagram) {
        this.clearDiff(selector.nodes);
        let selectorLeft = selector.wrapper.bounds.left;
        let selectorTop = selector.wrapper.bounds.top;
        let targetLeft = target.wrapper.bounds.left;
        let targetTop = target.wrapper.bounds.top;
        let diffLeft;
        let diffTop;
        const swimlane = diagram.nameTable[target.parentId];
        if (target.columnIndex === 0 && swimlane && swimlane.shape.orientation === 'Horizontal') {
            targetLeft += 50;
        }
        if (target.rowIndex === 1 && swimlane && swimlane.shape.orientation === 'Vertical') {
            targetTop += 50;
        }
        if (selectorLeft < targetLeft) {
            diffLeft = targetLeft - selectorLeft;
        }
        else {
            diffLeft = 0;
        }
        if (selectorTop < targetTop) {
            diffTop = targetTop - selectorTop;
        }
        else {
            diffTop = 0;
        }
        let nodes = selector.nodes;
        nodes.forEach(function (node, index) {
            node.diffX = diffLeft;
            node.diffY = diffTop;
        });
        return nodes;
    }
    //EJ2-59309-While drag the connected node the connector endPointChange event does not get trigger
    connectorEndPointChangeEvent(arg, snappedPoint) {
        let selectedElement = arg.source;
        if (selectedElement instanceof Selector && selectedElement.nodes.length > 0) {
            for (let i = 0; i < selectedElement.nodes.length; i++) {
                let node = selectedElement.nodes[parseInt(i.toString(), 10)];
                if (node && node.inEdges.length > 0) {
                    for (let j = 0; j < node.inEdges.length; j++) {
                        let connector = this.commandHandler.diagram.getObject(node.inEdges[parseInt(j.toString(), 10)]);
                        this.triggerEndPointEvent(connector, arg, snappedPoint, 'targetPointChange');
                    }
                }
                if (node && node.outEdges.length > 0) {
                    for (let j = 0; j < node.outEdges.length; j++) {
                        let connector = this.commandHandler.diagram.getObject(node.outEdges[parseInt(j.toString(), 10)]);
                        this.triggerEndPointEvent(connector, arg, snappedPoint, 'sourcePointChange');
                    }
                }
            }
        }
    }
    triggerEndPointEvent(connector, arg, snappedPoint, eventName) {
        let args = {
            connector: connector, state: arg.state, targetNode: connector.targetID, targetPort: connector.targetPortID,
            sourceNode: connector.sourceID, sourcePort: connector.sourcePortID, oldValue: { x: connector.targetPoint.x, y: connector.targetPoint.y },
            newValue: { x: connector.targetPoint.x + (snappedPoint ? snappedPoint.x : 0), y: connector.targetPoint.y + (snappedPoint ? snappedPoint.y : 0) }, cancel: arg.cancel
        };
        this.commandHandler.triggerEvent((eventName === 'targetPointChange') ? DiagramEvent.targetPointChange : DiagramEvent.sourcePointChange, args);
    }
    isSelectionHasConnector(args) {
        if (args.nodes && args.connectors && args.nodes.length > 0 && args.connectors.length > 0 &&
            (args.width !== this.undoElement.width || args.height !== this.undoElement.height)) {
            return true;
        }
        return false;
    }
    // private getBlazorPositionChangeEventArgs(args: IDraggingEventArgs | IBlazorDraggingEventArgs, target: IElement): any {
    // args = {
    //     source: cloneBlazorObject(args.source), state: args.state, oldValue: args.oldValue, newValue: args.newValue,
    //     target: getObjectType(target) === Connector ? { connector: cloneBlazorObject(target) }
    //         : { node: cloneBlazorObject(target) },
    //     targetPosition: this.currentPosition, allowDrop: true, cancel: false
    // };
    // return args as IBlazorDraggingEventArgs;
    // }
    /* tslint:disable */
    /**
     * @param args
     * @private
     */
    mouseMove(args) {
        super.mouseMove(args);
        let isSame = false;
        let object;
        object = this.commandHandler.renderContainerHelper(args.source) ||
            args.source;
        if (object instanceof Node || object instanceof Connector) {
            if (object instanceof Node) {
                if (object.offsetX === this.undoElement.nodes[0].offsetX &&
                    object.offsetY === this.undoElement.nodes[0].offsetY) {
                    isSame = true;
                }
            }
            else {
                if (Point.equals(object.sourcePoint, this.undoElement.connectors[0].sourcePoint) &&
                    Point.equals(object.targetPoint, this.undoElement.connectors[0].targetPoint)) {
                    isSame = true;
                }
            }
        }
        else {
            if (object.wrapper.offsetX === this.undoElement.wrapper.offsetX &&
                object.wrapper.offsetY === this.undoElement.wrapper.offsetY) {
                isSame = true;
            }
        }
        let oldValues;
        if (object) {
            oldValues = { offsetX: object.wrapper.offsetX, offsetY: object.wrapper.offsetY };
        }
        let arg = {
            source: object, state: 'Start', oldValue: oldValues, newValue: oldValues,
            target: args.target, targetPosition: args.position, allowDrop: true, cancel: false
        };
        arg = {
            source: cloneBlazorObject(object), state: 'Start', oldValue: cloneBlazorObject(oldValues),
            newValue: cloneBlazorObject(oldValues),
            target: args.target, targetPosition: args.position, allowDrop: arg.allowDrop, cancel: arg.cancel
        };
        //EJ2-909578: - Position change incorrect event args value for port dragging
        if (this.objectType === 'Port') {
            let port = this.getPort(args);
            if (port) {
                let nodePorts = this.undoElement.nodes.length > 0 ? this.undoElement.nodes[0].ports : [];
                let connPorts = this.undoElement.connectors.length > 0 ? this.undoElement.connectors[0].ports : [];
                for (let i = 0; i < nodePorts.length; i++) {
                    oldValues = { offsetX: port.offset.x, offsetY: port.offset.y };
                    if (port.offset.x !== nodePorts[parseInt(i.toString(), 10)].offset.x || port.offset.y !== nodePorts[parseInt(i.toString(), 10)].offset.y) {
                        isSame = false;
                        break;
                    }
                }
                for (let i = 0; i < connPorts.length; i++) {
                    oldValues = { offset: port.offset };
                    if (port.offset !== connPorts[parseInt(i.toString(), 10)].offset) {
                        isSame = false;
                        break;
                    }
                }
            }
            arg = {
                source: port, state: 'Start', oldValue: oldValues, newValue: oldValues,
                target: args.target, targetPosition: args.position, allowDrop: true, cancel: false
            };
        }
        //(EJ2-277624)-In the positionChange event, during the completed state, old and new values remain identical.
        if (!this.isStartAction) {
            this.intialValue = { offsetX: object.wrapper.offsetX, offsetY: object.wrapper.offsetY };
            if ((this.commandHandler.diagram.lineRoutingModule &&
                (this.commandHandler.diagram.constraints & DiagramConstraints.LineRouting)
                && (this.commandHandler.diagram.layout.type !== 'ComplexHierarchicalTree'))) {
                const INFLATE_MARGIN = 40;
                const nodeBounds = getBounds(object.wrapper);
                nodeBounds.Inflate(INFLATE_MARGIN);
                const nearbyObjects = this.commandHandler.diagram.spatialSearch.findObjects(nodeBounds);
                for (const item of nearbyObjects) {
                    if (item instanceof Connector && this.commandHandler.diagram.routedConnectors.indexOf(item.id) === -1) {
                        // this.commandHandler.diagram.lineConnector.push(item.id);
                    }
                }
            }
        }
        if (isSame && !isBlazor()) {
            this.commandHandler.triggerEvent(DiagramEvent.positionChange, arg);
            this.connectorEndPointChangeEvent(arg);
            this.isStartAction = true;
        }
        this.commandHandler.diagram.cancelPositionChange = arg.cancel;
        this.currentPosition = args.position;
        if (this.objectType !== 'Port') {
            const x = this.currentPosition.x - this.prevPosition.x;
            const y = this.currentPosition.y - this.prevPosition.y;
            const diffX = this.initialOffset.x + (this.currentPosition.x - this.prevPosition.x);
            const diffY = this.initialOffset.y + (this.currentPosition.y - this.prevPosition.y);
            this.commandHandler.dragOverElement(args, this.currentPosition);
            this.commandHandler.disConnect(args.source);
            this.commandHandler.removeSnap();
            let oldValues;
            let newValues;
            const snappedPoint = this.commandHandler.snapPoint(this.prevPosition, this.currentPosition, diffX, diffY);
            this.initialOffset.x = diffX - snappedPoint.x;
            this.initialOffset.y = diffY - snappedPoint.y;
            if (object) {
                oldValues = { offsetX: object.wrapper.offsetX, offsetY: object.wrapper.offsetY };
                newValues = {
                    offsetX: object.wrapper.offsetX + snappedPoint.x,
                    offsetY: object.wrapper.offsetY + snappedPoint.y
                };
            }
            if (this.currentTarget && args.target !== this.currentTarget) {
                this.commandHandler.removeChildFromBPmn(args.source, args.target, this.currentTarget);
            }
            this.currentTarget = args.target;
            let arg = {
                source: object, state: 'Progress', oldValue: oldValues, newValue: newValues,
                target: args.target, targetPosition: args.position, allowDrop: true, cancel: false
            };
            this.commandHandler.triggerEvent(DiagramEvent.positionChange, arg);
            this.commandHandler.diagram.cancelPositionChange = arg.cancel;
            this.canTrigger = true;
            this.connectorEndPointChangeEvent(arg, snappedPoint);
            if (!arg.cancel && !this.canCancel) {
                this.blocked = !this.commandHandler.dragSelectedObjects(snappedPoint.x, snappedPoint.y);
                const blocked = !(this.commandHandler.mouseOver(this.currentElement, this.currentTarget, this.currentPosition));
                this.blocked = this.blocked || blocked;
            }
            this.commandHandler.removeStackHighlighter();
            this.commandHandler.renderStackHighlighter(args);
            if (this.currentTarget && (args.source !== this.currentTarget) &&
                this.commandHandler.isDroppable(args.source, this.currentTarget) && args.source.id !== 'helper') {
                let object = (args.source instanceof Selector) ? args.source.nodes[0] : args.source;
                if ((!this.commandHandler.isParentAsContainer(object, true))
                    && (object.shape.type !== 'SwimLane' && !object.shape.isPhase)) {
                    if (this.currentTarget.isLane) {
                        this.commandHandler.renderStackHighlighter(args, this.currentTarget);
                    }
                    else {
                        this.commandHandler.drawHighlighter(this.currentTarget);
                    }
                }
            }
            else {
                this.commandHandler.removeHighlighter();
            }
            if (this.commandHandler.canEnableDefaultTooltip()) {
                const content = this.getTooltipContent(args.source);
                const contentTemp = function () {
                    return content;
                };
                this.commandHandler.showTooltip(args.source, args.position, initializeCSPTemplate(contentTemp), 'MoveTool', this.isTooltipVisible);
                this.isTooltipVisible = false;
            }
        }
        else {
            const matrix = identityMatrix();
            const node = args.source;
            let oldValues;
            let newValues;
            let nodePorts = this.undoElement.nodes.length > 0 ? this.undoElement.nodes[0].ports : [];
            let connPorts = this.undoElement.connectors.length > 0 ? this.undoElement.connectors[0].ports : [];
            let port = this.getPort(args);
            if (port) {
                for (let i = 0; i < nodePorts.length; i++) {
                    oldValues = { offsetX: port.offset.x, offsetY: port.offset.y };
                    break;
                }
                for (let i = 0; i < connPorts.length; i++) {
                    oldValues = { offset: port.offset };
                    break;
                }
            }
            rotateMatrix(matrix, -node.rotateAngle || -node.wrapper.rotateAngle, node.offsetX || node.wrapper.offsetX, node.offsetY || node.wrapper.offsetY);
            const prevPosition = transformPointByMatrix(matrix, { x: this.prevPosition.x, y: this.prevPosition.y });
            const position = transformPointByMatrix(matrix, { x: args.position.x, y: args.position.y });
            this.commandHandler.portDrag(args.source, args.sourceWrapper, position.x - prevPosition.x, position.y - prevPosition.y);
            //EJ2-909578: - Position change incorrect event args value for port dragging
            //Required correction on data type
            if (port) {
                for (let i = 0; i < nodePorts.length; i++) {
                    newValues = { offsetX: port.offset.x, offsetY: port.offset.y };
                    break;
                }
                for (let i = 0; i < connPorts.length; i++) {
                    newValues = { offset: port.offset };
                    break;
                }
            }
            let arg = {
                source: port, state: 'Progress', oldValue: oldValues, newValue: newValues,
                target: args.target, targetPosition: args.position, allowDrop: true, cancel: false
            };
            this.commandHandler.triggerEvent(DiagramEvent.positionChange, arg);
        }
        this.prevPosition = this.currentPosition;
        return !this.blocked;
    }
    getTooltipContent(node) {
        return 'X:' + Math.round(node.wrapper.bounds.x) + ' ' + 'Y:' + Math.round(node.wrapper.bounds.y);
    }
    /**
     * @param args
     * @private
     */
    mouseLeave(args) {
        this.mouseUp(args);
    }
    /**   @private  */
    endAction() {
        super.endAction();
        this.currentTarget = null;
        this.prevPosition = null;
    }
}
/**
 * Rotates the selected objects
 */
export class RotateTool extends ToolBase {
    constructor(commandHandler) {
        super(commandHandler, true);
        /** @private */
        this.rotateStart = false;
    }
    /**
     * @param args
     * @private
     */
    mouseDown(args) {
        this.undoElement = cloneObject(args.source);
        if (this.undoElement.nodes[0] && this.undoElement.nodes[0].children) {
            const objects = [];
            const nodes = this.commandHandler.getAllDescendants(this.undoElement.nodes[0], objects);
            for (let i = 0; i < nodes.length; i++) {
                const node = this.commandHandler.cloneChild(nodes[parseInt(i.toString(), 10)].id);
                this.childTable[nodes[parseInt(i.toString(), 10)].id] = cloneObject(node);
            }
        }
        super.mouseDown(args);
    }
    /**
     * @param args
     * @private
     */
    mouseUp(args) {
        const _super = Object.create(null, {
            mouseUp: { get: () => super.mouseUp }
        });
        return __awaiter(this, void 0, void 0, function* () {
            this.checkPropertyValue();
            let object;
            object = this.commandHandler.renderContainerHelper(args.source) || args.source;
            if (this.undoElement.rotateAngle !== object.wrapper.rotateAngle) {
                //942139: Rotation change event old and new value same
                const oldValue = { rotateAngle: this.undoElement.rotateAngle };
                const newValue = { rotateAngle: object.wrapper.rotateAngle };
                const arg = {
                    source: args.source, state: 'Completed', oldValue: oldValue,
                    newValue: newValue, cancel: false
                };
                this.commandHandler.triggerEvent(DiagramEvent.rotateChange, arg);
                let obj;
                obj = cloneObject(args.source);
                const entry = {
                    type: 'RotationChanged', redoObject: cloneObject(obj), undoObject: cloneObject(this.undoElement), category: 'Internal',
                    childTable: this.childTable
                };
                this.commandHandler.addHistoryEntry(entry);
                this.commandHandler.updateSelector();
                this.rotateStart = false;
            }
            // this.commandHandler.updateBlazorSelector();
            this.canCancel = undefined;
            this.tempArgs = undefined;
            _super.mouseUp.call(this, args);
        });
    }
    /**
     * @param args
     * @private
     */
    mouseMove(args) {
        super.mouseMove(args);
        let object;
        object = this.commandHandler.renderContainerHelper(args.source) || args.source;
        if (this.undoElement.rotateAngle === object.wrapper.rotateAngle && !this.rotateStart) {
            const oldValue = { rotateAngle: object.wrapper.rotateAngle };
            const arg = {
                source: args.source, state: 'Start', oldValue: oldValue, newValue: oldValue, cancel: false
            };
            this.commandHandler.triggerEvent(DiagramEvent.rotateChange, arg);
            this.rotateStart = true;
        }
        this.currentPosition = args.position;
        const refPoint = { x: object.wrapper.offsetX, y: object.wrapper.offsetY };
        let angle = Point.findAngle(refPoint, this.currentPosition) + 90;
        const snapAngle = this.commandHandler.snapAngle(angle);
        angle = snapAngle !== 0 ? snapAngle : angle;
        angle = (angle + 360) % 360;
        const oldValue = { rotateAngle: object.wrapper.rotateAngle };
        const newValue = { rotateAngle: angle };
        const arg = {
            source: args.source, state: 'Progress', oldValue: oldValue,
            newValue: newValue, cancel: false
        };
        const arg1 = {
            source: cloneBlazorObject(args.source), state: 'Progress', oldValue: cloneBlazorObject(oldValue),
            newValue: cloneBlazorObject(newValue), cancel: arg.cancel
        };
        this.commandHandler.triggerEvent(DiagramEvent.rotateChange, arg1);
        if (!arg1.cancel) {
            this.blocked = !(this.commandHandler.rotateSelectedItems(angle - object.wrapper.rotateAngle));
        }
        if (this.commandHandler.canEnableDefaultTooltip()) {
            const content = this.getTooltipContent(args.source);
            const contentTemp = function () {
                return content;
            };
            this.commandHandler.showTooltip(args.source, args.position, initializeCSPTemplate(contentTemp), 'RotateTool', this.isTooltipVisible);
            this.isTooltipVisible = false;
        }
        return !this.blocked;
    }
    getTooltipContent(node) {
        return Math.round((node.rotateAngle % 360)).toString() + '\xB0';
    }
    /**
     * @param args
     * @private
     */
    mouseLeave(args) {
        this.mouseUp(args);
    }
    /**   @private  */
    endAction() {
        super.endAction();
    }
}
/**
 * Scales the selected objects
 */
export class ResizeTool extends ToolBase {
    constructor(commandHandler, corner) {
        super(commandHandler, true);
        /** @private */
        this.resizeStart = false;
        /**   @private  */
        this.initialBounds = new Rect();
        this.canCancel = false;
        this.corner = corner;
    }
    /**
     * @param args
     * @private
     */
    mouseDown(args) {
        let oldValues;
        this.undoElement = cloneObject(args.source);
        this.undoParentElement = this.commandHandler.getSubProcess(args.source);
        this.undoContainerElement = this.commandHandler.getContainer(args.source);
        super.mouseDown(args);
        if (this.undoElement.nodes[0] && this.undoElement.nodes[0].children) {
            const elements = [];
            const nodes = this.commandHandler.getAllDescendants(this.undoElement.nodes[0], elements);
            for (let i = 0; i < nodes.length; i++) {
                const node = this.commandHandler.cloneChild(nodes[parseInt(i.toString(), 10)].id);
                this.childTable[nodes[parseInt(i.toString(), 10)].id] = cloneObject(node);
            }
        }
        this.commandHandler.checkSelection(args.source, this.corner);
        super.mouseDown(args);
        this.initialBounds.x = args.source.wrapper.offsetX;
        this.initialBounds.y = args.source.wrapper.offsetY;
        this.initialBounds.height = args.source.wrapper.actualSize.height;
        this.initialBounds.width = args.source.wrapper.actualSize.width;
    }
    /**
     * @param args
     * @param isPreventHistory
     * @param args
     * @param isPreventHistory
     * @private
     */
    mouseUp(args, isPreventHistory) {
        const _super = Object.create(null, {
            mouseUp: { get: () => super.mouseUp }
        });
        return __awaiter(this, void 0, void 0, function* () {
            this.checkPropertyValue();
            this.commandHandler.removeSnap();
            let object;
            this.commandHandler.updateSelector();
            object = this.commandHandler.renderContainerHelper(args.source) || args.source;
            if ((this.undoElement.offsetX !== object.wrapper.offsetX || this.undoElement.offsetY !== object.wrapper.offsetY ||
                this.undoElement.width !== object.wrapper.bounds.width || this.undoElement.height !== object.wrapper.bounds.height)) {
                const deltaValues = this.updateSize(args.source, this.currentPosition, this.prevPosition, this.corner, this.initialBounds);
                this.blocked = this.scaleObjects(deltaValues.width, deltaValues.height, this.corner, this.currentPosition, this.prevPosition, object);
                const oldValue = {
                    width: args.source.wrapper.actualSize.width, height: args.source.wrapper.actualSize.height
                };
                const arg = {
                    source: cloneBlazorObject(args.source), state: 'Completed',
                    oldValue: this.startValues, newValue: oldValue, cancel: false
                };
                this.commandHandler.triggerEvent(DiagramEvent.sizeChange, arg);
                const obj = cloneObject(args.source);
                const entry = {
                    type: 'SizeChanged', redoObject: cloneObject(obj), undoObject: cloneObject(this.undoElement), category: 'Internal',
                    childTable: this.childTable
                };
                this.resizeStart = false;
                if (!isPreventHistory) {
                    this.commandHandler.startGroupAction();
                    if (obj.nodes[0] && obj.nodes[0].processId) {
                        const entry = {
                            type: 'SizeChanged', redoObject: this.commandHandler.getSubProcess(args.source),
                            undoObject: this.undoParentElement, category: 'Internal'
                        };
                        this.commandHandler.addHistoryEntry(entry);
                    }
                    if (obj.nodes[0] && obj.nodes[0].parentId
                        && this.commandHandler.diagram.nameTable[obj.nodes[0].parentId].shape.type === 'Container') {
                        const entry = {
                            type: 'SizeChanged', redoObject: this.commandHandler.getContainer(args.source),
                            undoObject: this.undoContainerElement, category: 'Internal'
                        };
                        this.commandHandler.addHistoryEntry(entry);
                    }
                    //EJ2-934129 : Position of the subprocess changed during undo and redo operations
                    this.commandHandler.addHistoryEntry(entry);
                    this.commandHandler.endGroupAction();
                }
            }
            // this.commandHandler.updateBlazorSelector();
            _super.mouseUp.call(this, args);
            return !this.blocked;
        });
    }
    /**
     * @param args
     * @private
     */
    mouseMove(args) {
        super.mouseMove(args);
        let object;
        object = this.commandHandler.renderContainerHelper(args.source) || args.source;
        if (this.undoElement.offsetX === object.wrapper.offsetX && this.undoElement.offsetY === object.wrapper.offsetY && !this.resizeStart) {
            const oldValue = {
                width: args.source.wrapper.actualSize.width, height: args.source.wrapper.actualSize.height
            };
            //EJ2-866122 - Need to enhance the sizeChange Event
            this.startValues = oldValue;
            const arg = {
                source: args.source, state: 'Start', oldValue: oldValue, newValue: this.currentElement, cancel: false
            };
            this.commandHandler.triggerEvent(DiagramEvent.sizeChange, arg);
            this.resizeStart = true;
        }
        this.currentPosition = args.position;
        const x = this.currentPosition.x - this.startPosition.x;
        const y = this.currentPosition.y - this.startPosition.y;
        let changes = { x: x, y: y };
        changes = rotatePoint(-this.currentElement.wrapper.rotateAngle, undefined, undefined, changes);
        const sx = (this.currentElement.wrapper.actualSize.width + changes.x) / this.currentElement.wrapper.actualSize.width;
        const sy = (this.currentElement.wrapper.actualSize.height + changes.y) / this.currentElement.wrapper.actualSize.height;
        changes = this.getChanges(changes);
        this.commandHandler.removeSnap();
        const deltaValues = this.updateSize(args.source, this.startPosition, this.currentPosition, this.corner, this.initialBounds);
        this.blocked = !(this.scaleObjects(deltaValues.width, deltaValues.height, this.corner, this.startPosition, this.currentPosition, object));
        if (this.commandHandler.canEnableDefaultTooltip()) {
            const content = this.getTooltipContent(args.source);
            //Task 834121: Content-Security-Policy support for diagram
            const contentTemp = function () {
                return content;
            };
            this.commandHandler.showTooltip(args.source, args.position, initializeCSPTemplate(contentTemp), 'ResizeTool', this.isTooltipVisible);
            this.isTooltipVisible = false;
        }
        this.prevPosition = this.currentPosition;
        return !this.blocked;
    }
    /**
     * @param args
     * @private
     */
    mouseLeave(args) {
        this.mouseUp(args);
    }
    getTooltipContent(node) {
        return 'W:' + Math.round(node.wrapper.bounds.width) + ' ' + 'H:' + Math.round(node.wrapper.bounds.height);
    }
    getChanges(change) {
        switch (this.corner) {
            case 'ResizeEast':
                return { x: change.x, y: 0 };
            case 'ResizeSouthEast':
                return change;
            case 'ResizeSouth':
                return { x: 0, y: change.y };
            case 'ResizeNorth':
                return { x: 0, y: -change.y };
            case 'ResizeNorthEast':
                return { x: change.x, y: -change.y };
            case 'ResizeNorthWest':
                return { x: -change.x, y: -change.y };
            case 'ResizeWest':
                return { x: -change.x, y: 0 };
            case 'ResizeSouthWest':
                return { x: -change.x, y: change.y };
        }
        return change;
    }
    /**
     * Updates the size with delta width and delta height using scaling.
     */
    /**
     * Aspect ratio used to resize the width or height based on resizing the height or width
     *
     * @param deltaWidth
     * @param deltaHeight
     * @param corner
     * @param startPoint
     * @param endPoint
     * @param source
     * @param deltaWidth
     * @param deltaHeight
     * @param corner
     * @param startPoint
     * @param endPoint
     * @param source
     * @param deltaWidth
     * @param deltaHeight
     * @param corner
     * @param startPoint
     * @param endPoint
     * @param source
     * @param deltaWidth
     * @param deltaHeight
     * @param corner
     * @param startPoint
     * @param endPoint
     * @param source
     * @param deltaWidth
     * @param deltaHeight
     * @param corner
     * @param startPoint
     * @param endPoint
     * @param source
     * @param deltaWidth
     * @param deltaHeight
     * @param corner
     * @param startPoint
     * @param endPoint
     * @param source
     */
    scaleObjects(deltaWidth, deltaHeight, corner, startPoint, endPoint, source) {
        const selectedItem = this.commandHandler.diagram.selectedItems;
        if ((source instanceof Selector && source.nodes.length === 1 && source.nodes[0].constraints & NodeConstraints.AspectRatio) || (source instanceof Node && source.id === 'helper' && selectedItem.nodes.length === 1 && selectedItem.nodes[0].constraints & NodeConstraints.AspectRatio)) {
            if (corner === 'ResizeWest' || corner === 'ResizeEast' || corner === 'ResizeNorth' || corner === 'ResizeSouth') {
                if (!(deltaHeight === 1 && deltaWidth === 1)) {
                    deltaHeight = deltaWidth = Math.max(deltaHeight === 1 ? 0 : deltaHeight, deltaWidth === 1 ? 0 : deltaWidth);
                }
            }
            else {
                deltaHeight = deltaWidth = Math.max(deltaHeight, deltaWidth);
            }
        }
        const oldValue = {
            width: source.width, height: source.height
        };
        this.blocked = this.commandHandler.scaleSelectedItems(deltaWidth, deltaHeight, this.getPivot(this.corner));
        const newValue = {
            width: source.width, height: source.height
        };
        let arg;
        arg = { source: source, state: 'Progress', oldValue: oldValue, newValue: newValue, cancel: false };
        let arg1;
        arg1 = {
            source: cloneBlazorObject(source), state: 'Progress',
            oldValue: cloneBlazorObject(oldValue), newValue: cloneBlazorObject(newValue), cancel: arg.cancel
        };
        this.commandHandler.triggerEvent(DiagramEvent.sizeChange, arg1);
        if (arg1.cancel || this.canCancel) {
            this.commandHandler.scaleSelectedItems(1 / deltaWidth, 1 / deltaHeight, this.getPivot(this.corner));
        }
        return this.blocked;
    }
}
/**
 * Draws a node that is defined by the user
 */
export class NodeDrawingTool extends ToolBase {
    constructor(commandHandler, sourceObject) {
        super(commandHandler, true);
        this.sourceObject = sourceObject;
    }
    /**
     * @param args
     * @private
     */
    mouseDown(args) {
        super.mouseDown(args);
        this.inAction = true;
        this.commandHandler.setFocus();
        this.triggerElementDrawEvent(args.source, 'Start', 'Node', this.getShapeType(), true);
    }
    /**
     * @param args
     * @private
     */
    mouseMove(args) {
        super.mouseMove(args);
        let checkBoundaryConstraints;
        const node = {
            offsetX: this.currentPosition.x, width: 3, height: 3,
            offsetY: this.currentPosition.y
        };
        if (!this.drawingObject) {
            this.drawingObject = this.commandHandler.drawObject(node);
        }
        this.triggerElementDrawEvent(this.drawingObject, 'Progress', 'Node', this.getShapeType(), false);
        if (this.inAction && Point.equals(this.currentPosition, this.prevPosition) === false) {
            const rect = Rect.toBounds([this.prevPosition, this.currentPosition]);
            checkBoundaryConstraints = this.commandHandler.checkBoundaryConstraints(undefined, undefined, rect);
            if (checkBoundaryConstraints) {
                this.commandHandler.updateNodeDimension(this.drawingObject, rect);
            }
        }
        return checkBoundaryConstraints;
    }
    /**
     * @param args
     * @private
     */
    mouseUp(args) {
        this.checkPropertyValue();
        let checkBoundaryConstraints;
        const rect = Rect.toBounds([this.prevPosition, this.currentPosition]);
        checkBoundaryConstraints = this.commandHandler.checkBoundaryConstraints(undefined, undefined, rect);
        if (this.drawingObject && this.drawingObject instanceof Node) {
            this.commandHandler.addObjectToDiagram(this.drawingObject);
            this.triggerElementDrawEvent(this.drawingObject, 'Completed', 'Node', this.getShapeType(), false);
            this.drawingObject = null;
        }
        // this.commandHandler.updateBlazorSelector();
        super.mouseUp(args);
        this.inAction = false;
    }
    /**   @private  */
    endAction() {
        super.endAction();
    }
    /**
     * @param args
     * @private
     */
    mouseLeave(args) {
        if (this.inAction) {
            this.mouseUp(args);
        }
    }
}
/**
 * Draws a connector that is defined by the user
 */
export class ConnectorDrawingTool extends ConnectTool {
    constructor(commandHandler, endPoint, sourceObject) {
        super(commandHandler, endPoint);
        this.sourceObject = sourceObject;
    }
    /**
     * @param args
     * @private
     */
    mouseDown(args) {
        const _super = Object.create(null, {
            mouseDown: { get: () => super.mouseDown }
        });
        return __awaiter(this, void 0, void 0, function* () {
            _super.mouseDown.call(this, args);
            this.inAction = true;
            this.commandHandler.setFocus();
            this.triggerElementDrawEvent(args.source, 'Start', 'Connector', this.commandHandler.diagram.drawingObject.type, true);
        });
    }
    /**
     * @param args
     * @private
     */
    mouseMove(args) {
        this.commandHandler.enableServerDataBinding(false);
        if (this.inAction) {
            const connector = {
                sourcePoint: this.currentPosition, targetPoint: this.currentPosition
            };
            if (!this.drawingObject) {
                this.drawingObject = this.commandHandler.drawObject(connector);
            }
            args.source = this.drawingObject;
            this.triggerElementDrawEvent(args.source, 'Progress', 'Connector', this.drawingObject.type, false);
            //Bug 874781: Port Draw Connection is not proper with group node.
            if (args.actualObject && (args.actualObject.parentId || args.actualObject.children) && this.drawingObject.sourceID === ''
                && this.drawingObject.targetID === '') {
                this.setTarget(args);
            }
            if (((args.target && args.target instanceof Node) || (args.actualObject && args.sourceWrapper && checkPort(args.actualObject, args.sourceWrapper)))
                && (this.endPoint !== 'ConnectorTargetEnd' || (canInConnect(args.target)))) {
                //970513- Draw connector's target end from InConnect-Only ports
                if (args.sourceWrapper) {
                    const portId = (args.sourceWrapper.id).replace(args.actualObject.id + '_', '');
                    const targetPort = args.actualObject.ports.find((port) => port.id === portId);
                    const isInConnectOnly = targetPort && canPortInConnect(targetPort) && !(canPortOutConnect(targetPort));
                    if (isInConnectOnly) {
                        // Reverse: connect target end, move source end with mouse
                        this.endPoint = 'ConnectorTargetEnd';
                        args.sourceWrapper = null;
                        this.commandHandler.connect(this.endPoint, args);
                        this.endPoint = 'ConnectorSourceEnd';
                    }
                    else {
                        // Default: connect source end, move target end with mouse
                        this.commandHandler.connect(this.endPoint, args);
                        this.endPoint = 'ConnectorTargetEnd';
                    }
                }
            }
            else if (this.drawingObject.targetID === '') {
                this.endPoint = 'ConnectorTargetEnd';
            }
        }
        if (!this.inAction) {
            this.commandHandler.updateSelector();
            //EJ2-899368 : Highlighters for Connector Element Draw with userhandle updated wrongly
            if (args.source && !args.source.isLane && args.sourceWrapper) {
                this.commandHandler.renderHighlighter(args, true);
            }
        }
        super.mouseMove(args);
        this.commandHandler.enableServerDataBinding(true);
        return !this.blocked;
    }
    // Sets the target while drawing connector from the group node port or its children port.
    setTarget(args) {
        if (args.target) {
            if (!args.sourceWrapper.id.includes(args.target.id)) {
                if (args.target.parentId && args.sourceWrapper.id.includes(args.target.parentId)) {
                    args.target = this.commandHandler.diagram.nameTable[args.target.parentId];
                }
            }
        }
        else {
            if (!args.sourceWrapper.id.includes(args.actualObject.id)) {
                if (args.actualObject.parentId && args.sourceWrapper.id.includes(args.actualObject.parentId)) {
                    args.target = this.commandHandler.diagram.nameTable[args.actualObject.parentId];
                }
            }
            else {
                args.target = args.actualObject;
            }
        }
    }
    /**
     * @param args
     * @private
     */
    mouseUp(args) {
        const _super = Object.create(null, {
            mouseUp: { get: () => super.mouseUp }
        });
        return __awaiter(this, void 0, void 0, function* () {
            this.commandHandler.enableServerDataBinding(false);
            this.checkPropertyValue();
            if (this.drawingObject && this.drawingObject instanceof Connector) {
                this.commandHandler.addObjectToDiagram(this.drawingObject);
                this.triggerElementDrawEvent(this.drawingObject, 'Completed', 'Connector', this.drawingObject.type, false);
                this.drawingObject = null;
            }
            // this.commandHandler.updateBlazorSelector();
            this.inAction = false;
            this.commandHandler.enableServerDataBinding(true);
            _super.mouseUp.call(this, args);
        });
    }
    /**   @private  */
    endAction() {
        super.endAction();
    }
    /**
     * @param args
     * @private
     */
    mouseLeave(args) {
        if (this.inAction) {
            this.mouseUp(args);
        }
    }
}
export class TextDrawingTool extends ToolBase {
    constructor(commandHandler) {
        super(commandHandler, true);
    }
    /**
     * @param args
     * @private
     */
    mouseDown(args) {
        super.mouseDown(args);
        this.commandHandler.clearSelection();
        const node = {
            shape: { type: 'Text' },
            offsetX: this.currentPosition.x,
            offsetY: this.currentPosition.y
        };
        if (!args.source) {
            this.drawingNode = this.commandHandler.drawObject(node);
        }
    }
    /**
     * @param args
     * @private
     */
    mouseMove(args) {
        super.mouseMove(args);
        if (!this.drawingNode) {
            const node = {
                shape: { type: 'Text' }, offsetX: this.currentPosition.x, width: 30, height: 30,
                // EJ2-42640-Text size is different if Text Node is created over another diagram commited by sivakumar sekar
                // commanded style property and added it after the object is drawn
                // style: { strokeDashArray: '2 2', fill: 'transparent' },
                offsetY: this.currentPosition.y
            };
            this.drawingNode = this.commandHandler.drawObject(node);
            this.drawingNode.style.strokeDashArray = '2 2';
            this.drawingNode.style.fill = 'transparent';
        }
        else {
            this.drawingNode.style.strokeColor = 'black';
            this.drawingNode.style.strokeDashArray = '2 2';
            this.drawingNode.style.fill = 'transparent';
        }
        if (this.inAction && Point.equals(this.currentPosition, this.prevPosition) === false) {
            const rect = Rect.toBounds([this.prevPosition, this.currentPosition]);
            this.commandHandler.updateNodeDimension(this.drawingNode, rect);
        }
        return !this.blocked;
    }
    /**
     * @param args
     * @private
     */
    mouseUp(args) {
        this.checkPropertyValue();
        if (this.drawingNode) {
            this.drawingNode.style.strokeColor = 'none';
            this.drawingNode.style.fill = 'none';
        }
        else {
            this.drawingNode = args.source;
        }
        if (this.drawingNode && (this.drawingNode instanceof Node)) {
            this.commandHandler.addText(this.drawingNode, this.currentPosition);
        }
        super.mouseUp(args);
        this.inAction = false;
    }
    /**   @private  */
    endAction() {
        super.endAction();
    }
}
/**
 * Pans the diagram control on drag
 */
export class ZoomPanTool extends ToolBase {
    constructor(commandHandler, zoom) {
        super(commandHandler);
        this.zooming = zoom;
    }
    /**
     * @param args
     * @private
     */
    mouseDown(args) {
        super.mouseDown(args);
        this.inAction = true;
        this.commandHandler.setBlazorDiagramProps(true);
    }
    /**
     * @param args
     * @private
     */
    mouseMove(args) {
        super.mouseMove(args);
        if (this.inAction) {
            if (!this.zooming && Point.equals(this.currentPosition, this.prevPosition) === false) {
                const difX = this.currentPosition.x - this.prevPosition.x;
                const difY = this.currentPosition.y - this.prevPosition.y;
                this.commandHandler.scroll(difX, difY, this.currentPosition);
            }
            else if (args.moveTouches && args.moveTouches.length && args.moveTouches.length >= 2) {
                const startTouch0 = args.startTouches[0];
                const startTouch1 = args.startTouches[1];
                const moveTouch0 = args.moveTouches[0];
                const moveTouch1 = args.moveTouches[1];
                const scale = this.getDistance(moveTouch0, moveTouch1) / this.getDistance(startTouch0, startTouch1);
                const focusPoint = args.position;
                // 927527: Diagram flickers while performing pinch zoom
                if (scale !== 1) {
                    this.commandHandler.zoom(scale, 0, 0, focusPoint);
                }
                this.updateTouch(startTouch0, moveTouch0);
                this.updateTouch(startTouch1, moveTouch1);
            }
        }
        this.commandHandler.dataBinding();
        return !this.blocked;
    }
    /**
     * @param args
     * @private
     */
    mouseUp(args) {
        this.commandHandler.setBlazorDiagramProps(false);
        this.checkPropertyValue();
        this.commandHandler.updatePanState(false);
        super.mouseUp(args);
        this.inAction = false;
    }
    /**   @private  */
    endAction() {
        super.endAction();
    }
    getDistance(touch1, touch2) {
        const x = touch2.pageX - touch1.pageX;
        const y = touch2.pageY - touch1.pageY;
        return Math.sqrt((x * x) + (y * y));
    }
    updateTouch(startTouch, moveTouch) {
        startTouch.pageX = moveTouch.pageX;
        startTouch.pageY = moveTouch.pageY;
    }
}
/**
 * Animate the layout during expand and collapse
 */
export class ExpandTool extends ToolBase {
    constructor(commandHandler) {
        super(commandHandler, true);
    }
    /**
     * @param args
     * @private
     */
    mouseUp(args) {
        this.checkPropertyValue();
        this.commandHandler.initExpand(args);
        super.mouseUp(args);
    }
}
/**
 * Opens the annotation hypeLink at mouse up
 */
export class LabelTool extends ToolBase {
    constructor(commandHandler) {
        super(commandHandler, true);
    }
    /**
     * @param args
     * @private
     */
    mouseUp(args) {
        this.checkPropertyValue();
        let tab = '_blank';
        let windowOption = '';
        let windowHeight = window.innerHeight;
        let windowWidth = window.innerWidth;
        let screenTop = window.screenTop;
        let screenLeft = window.screenLeft;
        if (args.sourceWrapper.hyperlink.hyperlinkOpenState === 'CurrentTab') {
            tab = '_self';
        }
        else if (args.sourceWrapper.hyperlink.hyperlinkOpenState === 'NewWindow') {
            windowOption = 'height=' + windowHeight + ',width=' + windowWidth + ',top=' + screenTop + ',left=' + screenLeft;
        }
        const win = window.open(args.sourceWrapper.hyperlink.link, tab, windowOption);
        win.focus();
        super.mouseUp(args);
    }
}
/**
 * Draws a Polygon shape node dynamically using polygon Tool
 */
export class PolygonDrawingTool extends ToolBase {
    constructor(commandHandler) {
        super(commandHandler, true);
    }
    /**
     * @param args
     * @private
     */
    mouseDown(args) {
        super.mouseDown(args);
        this.inAction = true;
        if (!this.drawingObject) {
            this.startPoint = { x: this.startPosition.x, y: this.startPosition.y };
            const node = {
                offsetX: this.currentPosition.x,
                offsetY: this.currentPosition.y,
                width: 5, height: 5,
                // 916722: Apply custom style to polygon shape by removing the default style during polygon drawing.
                shape: {
                    type: 'Basic',
                    shape: 'Polygon',
                    points: [{ x: this.startPoint.x, y: this.startPoint.y }, { x: this.currentPosition.x, y: this.currentPosition.y }]
                }
            };
            // 920152: elementDraw event not triggered for Polygon Drawing tool
            this.triggerElementDrawEvent(args.source, 'Start', 'Node', this.getShapeType(), true);
            this.drawingObject = this.commandHandler.drawObject(node);
        }
        else {
            let pt;
            const obj = this.drawingObject.shape;
            pt = obj.points[obj.points.length - 1];
            pt = { x: pt.x, y: pt.y };
            this.drawingObject.shape.points.push(pt);
        }
    }
    /**
     * @param args
     * @private
     */
    mouseMove(args) {
        super.mouseMove(args);
        if (this.inAction) {
            const obj = this.drawingObject.shape;
            obj.points[obj.points.length - 1].x = this.currentPosition.x;
            obj.points[obj.points.length - 1].y = this.currentPosition.y;
            this.drawingObject.wrapper.children[0].data = getPolygonPath(this.drawingObject.shape.points);
            // 920152: elementDraw event not triggered for Polygon Drawing tool
            this.triggerElementDrawEvent(this.drawingObject, 'Progress', 'Node', this.getShapeType(), false);
            if (this.inAction && Point.equals(this.currentPosition, this.prevPosition) === false) {
                const region = Rect.toBounds(this.drawingObject.shape.points);
                this.commandHandler.updateNodeDimension(this.drawingObject, region);
            }
        }
        return true;
    }
    /**
     * @param args
     * @param dblClickArgs
     * @param args
     * @param dblClickArgs
     * @private
     */
    mouseUp(args, dblClickArgs) {
        this.checkPropertyValue();
        super.mouseMove(args);
        if (this.inAction) {
            this.inAction = false;
            this.commandHandler.addObjectToDiagram(this.drawingObject);
            // 920152: elementDraw event not triggered for Polygon Drawing tool
            this.triggerElementDrawEvent(this.drawingObject, 'Completed', 'Node', this.getShapeType(), false);
        }
        this.endAction();
    }
    /**
     * @param args
     * @private
     */
    mouseWheel(args) {
        super.mouseWheel(args);
        this.mouseMove(args);
    }
    /**   @private  */
    endAction() {
        this.inAction = false;
        this.drawingObject = null;
    }
}
/**
 * Draws a PolyLine Connector dynamically using PolyLine Drawing Tool
 */
export class PolyLineDrawingTool extends ToolBase {
    constructor(commandHandler) {
        super(commandHandler, true);
        this.connectorEndPoint = 'ConnectorSourceEnd';
    }
    /**
     * @param args
     * @private
     */
    mouseMove(args) {
        super.mouseMove(args);
        let canConnect = false;
        if (this.inAction) {
            const obj = this.drawingObject;
            obj.targetPoint = this.currentPosition;
            // 920152: elementDraw event not triggered for Polyline Drawing tool
            // 927554: ElementDraw Arguments updated wrongly for Polyline drawing
            this.triggerElementDrawEvent(this.drawingObject, 'Progress', 'Connector', this.commandHandler.diagram.drawingObject.type, false);
            this.commandHandler.updateConnectorPoints(obj);
            // 962382: Drawing polyLine from port and node
            args.source = this.drawingObject;
            if (args.target && !args.target.isLane && args.targetWrapper) {
                canConnect = this.canConnect(args.targetWrapper, args.target, this.connectorEndPoint, 'In');
                if (canConnect) {
                    this.commandHandler.renderHighlighter(args, this.connectorEndPoint === 'ConnectorSourceEnd');
                }
            }
            if (this.connectorEndPoint === 'ConnectorSourceEnd' && (args.actualObject && args.actualObject instanceof Node && args.sourceWrapper)) {
                if (args.source.sourceID === '' || args.source.sourceID !== args.source.targetID) {
                    this.commandHandler.connect(this.connectorEndPoint, args);
                }
            }
            this.connectorEndPoint = 'ConnectorTargetEnd';
        }
        if (!this.inAction) {
            this.commandHandler.updateSelector();
            if (args.source && !args.source.isLane && args.sourceWrapper) {
                canConnect = this.canConnect(args.sourceWrapper, args.source, this.connectorEndPoint, 'Out');
                if (canConnect) {
                    this.commandHandler.renderHighlighter(args, true);
                }
            }
        }
        return true;
    }
    /**
     * @param args
     * @private
     */
    mouseDown(args) {
        super.mouseDown(args);
        this.inAction = true;
        if (!this.drawingObject) {
            const connector = {
                id: 'Connector',
                type: 'Straight',
                sourcePoint: this.currentPosition,
                targetPoint: this.currentPosition
            };
            // 920152: elementDraw event not triggered for Polyline Drawing tool
            this.triggerElementDrawEvent(args.source, 'Start', 'Connector', this.commandHandler.diagram.drawingObject.type, true);
            if (this.inAction) {
                this.drawingObject = this.commandHandler.drawObject(connector);
            }
        }
        else {
            const drawObject = this.drawingObject;
            let segment;
            segment = new StraightSegment(drawObject, 'segments', { type: 'Straight' }, true);
            segment.point = this.currentPosition;
            drawObject.segments[drawObject.segments.length - 1] = segment;
        }
    }
    /**
     * @param args
     * @private
     */
    mouseWheel(args) {
        super.mouseWheel(args);
        this.mouseMove(args);
    }
    /**
     * @param args
     * @private
     */
    mouseUp(args) {
        this.checkPropertyValue();
        super.mouseMove(args);
        if (this.inAction) {
            if (this.drawingObject) {
                // 962382: Drawing polyLine from port and node
                if (this.connectorEndPoint === 'ConnectorTargetEnd') {
                    const eventhandler = this.commandHandler.diagram.eventHandler;
                    let hoverObject = eventhandler.hoverElement;
                    if (hoverObject) {
                        hoverObject = hoverObject instanceof Node ? hoverObject : hoverObject.parentObj;
                        const padding = eventhandler.getConnectorPadding(args);
                        /* eslint-disable max-len */
                        let wrapper = this.commandHandler.diagram.findElementUnderMouse(hoverObject, this.currentPosition, this.commandHandler.diagram, padding);
                        const isMouseAboveObject = wrapper && wrapper.bounds.containsPoint(this.currentPosition, padding);
                        if (isMouseAboveObject) {
                            args.actualObject = hoverObject;
                            args.target = hoverObject;
                            args.targetWrapper = wrapper;
                            if (this.drawingObject.sourceID !== args.target.id) {
                                this.commandHandler.connect(this.connectorEndPoint, args);
                            }
                        }
                    }
                }
                const drawObject = this.drawingObject;
                drawObject.segments[drawObject.segments.length - 1].point = { x: 0, y: 0 };
                this.commandHandler.addObjectToDiagram(this.drawingObject);
                // 920152: elementDraw event not triggered for Polyline Drawing tool
                // 927554: ElementDraw Arguments updated wrongly for Polyline drawing
                this.triggerElementDrawEvent(this.drawingObject, 'Completed', 'Connector', this.commandHandler.diagram.drawingObject.type, false);
            }
        }
        this.endAction();
    }
    /**   @private  */
    endAction() {
        this.drawingObject = null;
        this.inAction = false;
    }
    /**   @private  */
    canConnect(wrapper, object, endpoint, connectionType) {
        let canConnect = false;
        const targetObject = this.commandHandler.findTarget(wrapper, object, endpoint === 'ConnectorSourceEnd', true);
        if (targetObject instanceof PointPort) {
            canConnect = Boolean(connectionType === 'In' ?
                (this.connectorEndPoint === 'ConnectorTargetEnd' && canPortInConnect(targetObject)) :
                (this.connectorEndPoint === 'ConnectorSourceEnd' && canPortOutConnect(targetObject)));
        }
        else if (targetObject instanceof Node) {
            canConnect = Boolean(connectionType === 'In' ?
                (this.connectorEndPoint === 'ConnectorTargetEnd' && canInConnect(targetObject)) :
                (this.connectorEndPoint === 'ConnectorSourceEnd' && canOutConnect(targetObject)));
        }
        return canConnect;
    }
}
export class LabelDragTool extends ToolBase {
    constructor(commandHandler) {
        super(commandHandler, true);
    }
    /**
     * @param args
     * @private
     */
    mouseDown(args) {
        this.inAction = true;
        this.undoElement = cloneObject(args.source);
        this.annotationId = args.sourceWrapper.id;
        super.mouseDown(args);
    }
    /**
     * @param args
     * @private
     */
    mouseMove(args) {
        super.mouseMove(args);
        let difx = this.currentPosition.x - this.prevPosition.x;
        let dify = this.currentPosition.y - this.prevPosition.y;
        const node = args.source;
        if (node instanceof Node) {
            const matrix = identityMatrix();
            rotateMatrix(matrix, -node.rotateAngle, 0, 0);
            const diff = transformPointByMatrix(matrix, { x: difx, y: dify });
            difx = diff.x;
            dify = diff.y;
        }
        if (this.inAction) {
            this.commandHandler.labelDrag(args.source, args.sourceWrapper, difx, dify);
            this.commandHandler.updateSelector();
        }
        this.prevPosition = this.currentPosition;
        return !this.blocked;
    }
    /**
     * @param args
     * @private
     */
    mouseUp(args) {
        this.checkPropertyValue();
        const redoValue = args.source;
        this.inAction = false;
        const entryValue = {
            type: 'AnnotationPropertyChanged',
            objectId: this.annotationId, undoObject: cloneObject(this.undoElement),
            category: 'Internal', redoObject: cloneObject(redoValue)
        };
        this.commandHandler.addHistoryEntry(entryValue);
        super.mouseUp(args);
    }
    /**
     * @param args
     * @private
     */
    mouseLeave(args) {
        this.mouseUp(args);
    }
}
export class LabelResizeTool extends ToolBase {
    constructor(commandHandler, corner) {
        super(commandHandler, true);
        this.corner = corner;
    }
    /**
     * @param args
     * @private
     */
    mouseDown(args) {
        this.inAction = true;
        const object = (args.source.nodes.length) ?
            args.source.nodes[0] : args.source.connectors[0];
        this.annotationId = args.source.wrapper.children[0].id;
        this.undoElement = cloneObject(object);
        const annotation = args.source.wrapper.children[0];
        this.initialBounds = {
            x: annotation.offsetX,
            y: annotation.offsetY,
            width: annotation.actualSize.width,
            height: annotation.actualSize.height
        };
        super.mouseDown(args);
    }
    /**
     * @param args
     * @private
     */
    mouseMove(args) {
        super.mouseMove(args);
        if (this.inAction) {
            this.resizeObject(args);
        }
        return !this.blocked;
    }
    /**
     * @param args
     * @private
     */
    mouseUp(args) {
        this.checkPropertyValue();
        const redoObject = (args.source.nodes.length) ?
            args.source.nodes[0] : args.source.connectors[0];
        this.inAction = false;
        const entry = {
            type: 'AnnotationPropertyChanged', objectId: this.annotationId,
            redoObject: cloneObject(redoObject), undoObject: cloneObject(this.undoElement), category: 'Internal'
        };
        this.commandHandler.addHistoryEntry(entry);
        super.mouseUp(args);
    }
    /**
     * @param args
     * @private
     */
    mouseLeave(args) {
        this.mouseUp(args);
    }
    /**
     * @param args
     * @private
     */
    resizeObject(args) {
        let object;
        object = (args.source.nodes.length) ? args.source.nodes[0] : args.source.connectors[0];
        const textElement = args.source.wrapper.children[0];
        let deltaWidth;
        let deltaHeight;
        const center = { x: textElement.offsetX, y: textElement.offsetY };
        let rotateAngle = textElement.rotateAngle;
        rotateAngle += (object instanceof Node) ? object.rotateAngle : 0;
        rotateAngle = (rotateAngle + 360) % 360;
        const trans = identityMatrix();
        rotateMatrix(trans, rotateAngle, center.x, center.y);
        const corner = this.corner.slice(5);
        const pivot = this.updateSize(textElement, this.startPosition, this.currentPosition, corner, this.initialBounds, rotateAngle);
        const x = textElement.offsetX - textElement.actualSize.width * textElement.pivot.x;
        const y = textElement.offsetY - textElement.actualSize.height * textElement.pivot.y;
        let pivotPoint = this.getPivot(corner);
        pivotPoint = { x: x + textElement.actualSize.width * pivotPoint.x, y: y + textElement.actualSize.height * pivotPoint.y };
        const point = transformPointByMatrix(trans, pivotPoint);
        pivot.x = point.x;
        pivot.y = point.y;
        deltaWidth = pivot.width;
        deltaHeight = pivot.height;
        deltaWidth = (deltaWidth < 0) ? 1 : deltaWidth;
        deltaHeight = (deltaHeight < 0) ? 1 : deltaHeight;
        this.commandHandler.labelResize(object, args.source.annotation, deltaWidth, deltaHeight, pivot, args.source);
        this.commandHandler.updateSelector();
    }
}
export class LabelRotateTool extends ToolBase {
    constructor(commandHandler) {
        super(commandHandler, true);
    }
    /**
     * @param args
     * @private
     */
    mouseDown(args) {
        this.inAction = true;
        this.annotationId = args.source.wrapper.children[0].id;
        const object = (args.source.nodes.length) ?
            args.source.nodes[0] : args.source.connectors[0];
        this.undoElement = cloneObject(object);
        super.mouseDown(args);
    }
    /**
     * @param args
     * @private
     */
    mouseMove(args) {
        super.mouseMove(args);
        if (args.source) {
            if (this.inAction) {
                const object = args.source.nodes[0] ? args.source.nodes[0] :
                    args.source.connectors[0];
                let annotation;
                annotation = (args.source.annotation);
                this.commandHandler.labelRotate(object, annotation, this.currentPosition, args.source);
                this.commandHandler.updateSelector();
            }
        }
        this.prevPosition = this.currentPosition;
        return !this.blocked;
    }
    /**
     * @param args
     * @private
     */
    mouseUp(args) {
        this.checkPropertyValue();
        this.inAction = false;
        const redoEntry = (args.source.nodes.length) ?
            args.source.nodes[0] : args.source.connectors[0];
        const entryObject = {
            type: 'AnnotationPropertyChanged', objectId: this.annotationId,
            redoObject: cloneObject(redoEntry),
            undoObject: cloneObject(this.undoElement), category: 'Internal'
        };
        this.commandHandler.addHistoryEntry(entryObject);
        super.mouseUp(args);
    }
    /**
     * @param args
     * @private
     */
    mouseLeave(args) {
        this.mouseUp(args);
    }
}
/**
 * EJ2-33302 - Freehand drawing support in diagram control.
 */
export class FreeHandTool extends ToolBase {
    constructor(commandHandler) {
        super(commandHandler, true);
    }
    /**
     * mouseMove - Collect the points using current mouse position and convert it into pathData.
     * @param args
     * @private
     */
    mouseMove(args) {
        super.mouseMove(args);
        if (this.inAction) {
            const obj = this.drawingObject.shape;
            let pt = this.currentPosition;
            obj.points.push(pt);
            this.drawingObject.wrapper.children[0].data = getFreeHandPath(this.drawingObject.shape.points);
            obj.data = getFreeHandPath(obj.points);
            // 920152: elementDraw event not triggered for Freehand Drawing tool
            this.triggerElementDrawEvent(args.source, 'Progress', 'Connector', this.drawingObject.type, false);
            if (this.inAction && Point.equals(this.currentPosition, this.prevPosition) === false) {
                const region = Rect.toBounds(this.drawingObject.shape.points);
                this.commandHandler.updateNodeDimension(this.drawingObject, region);
            }
        }
        return true;
    }
    /**
     * @param args
     * @private
     */
    mouseDown(args) {
        super.mouseDown(args);
        this.inAction = true;
        this.startPoint = { x: this.startPosition.x, y: this.startPosition.y };
        const node = {
            offsetX: this.currentPosition.x,
            offsetY: this.currentPosition.y,
            width: 5, height: 5,
            style: { strokeColor: 'black', strokeWidth: 1, fill: 'transparent' },
            shape: {
                type: 'Path',
                points: [{ x: this.startPoint.x, y: this.startPoint.y }, { x: this.currentPosition.x, y: this.currentPosition.y }]
            }
        };
        // 920152: elementDraw event not triggered for Freehand Drawing tool
        this.triggerElementDrawEvent(args.source, 'Start', 'Connector', this.commandHandler.diagram.drawingObject.type, true);
        this.drawingObject = this.commandHandler.drawObject(node);
    }
    /**
     * mouseUp - Remove the drawn object. Reduce and smoothen the collected points and create
     * a bezier connector using the smoothened points.
     * @param args
     * @private
     */
    mouseUp(args) {
        this.checkPropertyValue();
        let tolerance = 10;
        let smoothValue = 0.5;
        if (this.inAction) {
            this.inAction = false;
            let obj = this.drawingObject.shape;
            let points = obj.points;
            this.commandHandler.addObjectToDiagram(this.drawingObject);
            let prevId = this.drawingObject.id;
            let prevObj = this.commandHandler.diagram.nameTable[`${prevId}`];
            this.commandHandler.diagram.remove(prevObj);
            points = this.pointReduction(points, tolerance);
            // 927557: controlPointsVisibility Property values not considered in Freehand drawing
            let bezierSettings = {};
            if (this.commandHandler.diagram.drawingObject.bezierSettings) {
                bezierSettings = this.commandHandler.diagram.drawingObject.bezierSettings;
            }
            if (bezierSettings.allowSegmentsReset === undefined) {
                bezierSettings.allowSegmentsReset = false;
            }
            //EJ2-69816 - Added below code to set the allow segment reset as false to avoid the unwanted segment reset.
            const newObj = {
                id: 'newConnector' + randomId(), type: 'Bezier',
                sourcePoint: { x: points[0].x, y: points[0].y }, targetPoint: { x: points[points.length - 1].x, y: points[points.length - 1].y },
                //EJ2-873504[BUG]- Source and target decorator for free hand connector is not rendered.
                segments: [], bezierSettings: bezierSettings
            };
            this.drawingObject = this.commandHandler.drawObject(newObj);
            this.drawingObject = this.bezierCurveSmoothness(points, smoothValue, this.drawingObject, obj);
            this.commandHandler.updateConnectorPoints(this.drawingObject);
            this.commandHandler.addObjectToDiagram(this.drawingObject);
            // 920152: elementDraw event not triggered for Freehand Drawing tool
            // 927554: ElementDraw Arguments updated wrongly for Freehand drawing
            this.triggerElementDrawEvent(this.drawingObject, 'Completed', 'Connector', this.commandHandler.diagram.drawingObject.type, false);
            //(EJ2-70838)- Added code to resolve style property not added dynamically for freehand connector
            // Added code to resolve style property not added dynamically for freehand connector
            super.mouseUp(args);
        }
    }
    /**
     * Reduce the collected points based on tolerance value.
     * @param points
     * @param tolerance
     * @returns points
     */
    pointReduction(points, tolerance) {
        if (points === null || points.length < 3) {
            return points;
        }
        let firstPoint = 0;
        let lastPoint = points.length - 1;
        let pointIndex = [];
        pointIndex.push(firstPoint);
        pointIndex.push(lastPoint);
        while (points[parseInt(firstPoint.toString(), 10)] === (points[parseInt(lastPoint.toString(), 10)])) {
            lastPoint--;
        }
        this.reduction(points, firstPoint, lastPoint, tolerance, pointIndex);
        let returnedPoints = [];
        pointIndex.sort(function (a, b) { return a - b; });
        pointIndex.forEach(element => {
            returnedPoints.push(points[parseInt(element.toString(), 10)]);
        });
        return returnedPoints;
    }
    reduction(points, firstPoint, lastPoint, tolerance, pointIndex) {
        let maxDistance = 0;
        let largestPointIndex = 0;
        for (let i = firstPoint; i < lastPoint; i++) {
            let distance = this.perpendicularDistance(points[parseInt(firstPoint.toString(), 10)], points[parseInt(lastPoint.toString(), 10)], points[parseInt(i.toString(), 10)]);
            if (distance > maxDistance) {
                maxDistance = distance;
                largestPointIndex = i;
            }
        }
        if (maxDistance > tolerance && largestPointIndex !== 0) {
            pointIndex.push(largestPointIndex);
            this.reduction(points, firstPoint, largestPointIndex, tolerance, pointIndex);
            this.reduction(points, largestPointIndex, lastPoint, tolerance, pointIndex);
        }
    }
    /**
     * Calculate the perpendicular distance of each point with first and last points
     * @param point1
     * @param point2
     * @param point3
     * @returns
     */
    perpendicularDistance(point1, point2, point3) {
        let area = Math.abs(.5 * ((point1.x * point2.y - point2.x * point1.y) +
            (point2.x * point3.y - point3.x * point2.y) + (point3.x * point1.y - point1.x * point3.y)));
        let base = Math.sqrt(Math.pow(point1.x - point2.x, 2) + Math.pow(point1.y - point2.y, 2));
        let height = area / base * 2;
        return height;
    }
    /**
     * Smoothen the bezier curve based on the points and smoothValue.
     * @param points
     * @param smoothValue
     * @param drawingObject
     * @param obj
     * @returns drawingObject
     */
    bezierCurveSmoothness(points, smoothValue, drawingObject, obj) {
        if (points.length < 3) {
            return drawingObject;
        }
        for (let i = 0; i < points.length - 1; i++) {
            let pointx1 = points[parseInt(i.toString(), 10)].x;
            let pointy1 = points[parseInt(i.toString(), 10)].y;
            let pointx2 = points[i + 1].x;
            let pointy2 = points[i + 1].y;
            let pointx0;
            let pointy0;
            if (i === 0) {
                let previousPoint = points[parseInt(i.toString(), 10)];
                pointx0 = previousPoint.x;
                pointy0 = previousPoint.y;
            }
            else {
                pointx0 = points[i - 1].x;
                pointy0 = points[i - 1].y;
            }
            let pointx3;
            let pointy3;
            if (i === points.length - 2) {
                let nextPoint = points[i + 1];
                pointx3 = nextPoint.x;
                pointy3 = nextPoint.y;
            }
            else {
                pointx3 = points[i + 2].x;
                pointy3 = points[i + 2].y;
            }
            let xc1 = (pointx0 + pointx1) / 2.0;
            let yc1 = (pointy0 + pointy1) / 2.0;
            let xc2 = (pointx1 + pointx2) / 2.0;
            let yc2 = (pointy1 + pointy2) / 2.0;
            let xc3 = (pointx2 + pointx3) / 2.0;
            let yc3 = (pointy2 + pointy3) / 2.0;
            let point0 = {};
            let point1 = {};
            let point2 = {};
            let point3 = {};
            point0.x = pointx0;
            point0.y = pointy0;
            point1.x = pointx1;
            point1.y = pointy1;
            point2.x = pointx2;
            point2.y = pointy2;
            point3.x = pointx3;
            point3.y = pointy3;
            let len1 = Point.findLength(point0, point1);
            let len2 = Point.findLength(point1, point2);
            let len3 = Point.findLength(point2, point3);
            let k1 = len1 / (len1 + len2);
            let k2 = len2 / (len2 + len3);
            let xm1 = xc1 + (xc2 - xc1) * k1;
            let ym1 = yc1 + (yc2 - yc1) * k1;
            let xm2 = xc2 + (xc3 - xc2) * k2;
            let ym2 = yc2 + (yc3 - yc2) * k2;
            let Controlpointx1 = xm1 + (xc2 - xm1) * smoothValue + pointx1 - xm1;
            let Controlpointy1 = ym1 + (yc2 - ym1) * smoothValue + pointy1 - ym1;
            let Controlpointx2 = xm2 + (xc2 - xm2) * smoothValue + pointx2 - xm2;
            let Controlpointy2 = ym2 + (yc2 - ym2) * smoothValue + pointy2 - ym2;
            let segment = new BezierSegment(obj, 'segments', { type: 'Bezier' }, true);
            let cnPt1 = { x: Controlpointx1, y: Controlpointy1 };
            let cnPt2 = { x: Controlpointx2, y: Controlpointy2 };
            let segSourcePoint = { x: pointx1, y: pointy1 };
            let segTargetPoint = { x: pointx2, y: pointy2 };
            segment.type = 'Bezier';
            drawingObject.segments[parseInt(i.toString(), 10)] = segment;
            if (i === 0) {
                cnPt1 = { x: pointx1, y: pointy1 };
            }
            if (i === points.length - 2) {
                cnPt2 = { x: pointx2, y: pointy2 };
            }
            drawingObject.segments[parseInt(i.toString(), 10)].vector1 = { angle: findAngle(segSourcePoint, cnPt1), distance: Point.findLength(segSourcePoint, cnPt1) };
            drawingObject.segments[parseInt(i.toString(), 10)].vector2 = { angle: findAngle(segTargetPoint, cnPt2), distance: Point.findLength(segTargetPoint, cnPt2) };
            drawingObject.segments[parseInt(i.toString(), 10)].point = segTargetPoint;
        }
        return drawingObject;
    }
}
